// ───────────────────────────────────────────────────────────────────────────
// Butterfly Camp — shared content store (window.BCStore)
//
// One source of truth for everything the admin can edit. The public site reads
// it; the admin writes it. Data lives on the backend in content.json and syncs
// live over Server-Sent Events.
//
// Loaded as the FIRST <script type="text/babel"> in both HTML files, so every
// other module can call BCStore.useStore().
// ───────────────────────────────────────────────────────────────────────────
(function () {
  const { useState, useEffect } = React;

  // ── DEFAULTS — the hand-authored fallback content ──────────────────────────
  // A complete, forward-compatible base: every key the app reads has a default
  // here, so newly added fields keep working even before the next bake. At
  // runtime this is overlaid by BAKED (a full snapshot of the live content.json,
  // generated by `npm run bake`) — see EFFECTIVE_DEFAULTS below. That overlay is
  // what makes EVERYTHING the admin edits (text, pages, events, gallery, blog…)
  // survive a fresh deploy and "Reset to defaults". Run `npm run bake` after
  // editing the site in the admin, then commit. The /uploads images referenced
  // by the snapshot are committed to the repo (bake force-adds them).
  const DEFAULTS = {
    "version": 1,
    "hero": {
      "badge": "Est. 1997 - Family Owned & Operated Campground",
      "headline": "Where Families\nFind Their\nWild Side",
      "sub": "15 acres of shaded campsites, cozy cabins, and outdoor adventure in the heart of New Jersey. Open April 3rd through October 18th.",
      "cta": "See accommodations",
      "ctaPrimary": "Book by phone",
      "photo": "/uploads/img-mq5zplpf-008dc6.webp",
      "ctaPrimaryLink": {
        "action": "phone",
        "value": ""
      },
      "ctaLink": {
        "action": "page",
        "value": "rv"
      }
    },
    "brand": {
      "name": "Butterfly Camping",
      "sub": "Resort Est. 1997 · Jackson NJ",
      "logo": "/uploads/img-mq2nt827-b0e95f.webp"
    },
    "footer": {
      "tagline": "15 acres of shaded campsites, cozy cabins, and outdoor adventure in the heart of New Jersey. Open April 3rd through October 18th."
    },
    "home": {
      "about": {
        "eyebrow": "About the resort",
        "title": "A quiet stretch\nof NJ pine.",
        "body": "Butterfly Camping Resort is a peaceful retreat located in Jackson Township, New Jersey, offering a relaxing outdoor getaway for families, campers, and travelers. Surrounded by nature, the resort provides spacious campsites, RV accommodations, and a welcoming environment for weekend trips or extended stays.",
        "stats": [
          [
            "128",
            "RV sites"
          ],
          [
            "6",
            "Tent sites"
          ],
          [
            "8",
            "Cabins"
          ]
        ],
        "photos": [
          {
            "tone": "pine",
            "label": "",
            "src": "/uploads/img-mq633sfj-d9bcaf.jpg"
          },
          {
            "tone": "water",
            "label": "",
            "src": "/uploads/img-mq633wwv-967bd3.jpg"
          },
          {
            "tone": "meadow",
            "label": "Honey Bear's Birthday Ride",
            "src": "/uploads/img-mq624ds7-91ede4.webp"
          }
        ],
        "features": [
          {
            "id": "feat-rated",
            "icon": "star",
            "title": "Highly Rated",
            "sub": "Loved by guests"
          },
          {
            "id": "feat-family",
            "icon": "leaf",
            "title": "Family-Owned",
            "sub": "Since 1997"
          },
          {
            "id": "feat-pets",
            "icon": "check",
            "title": "Pet Friendly",
            "sub": "Leashed pups welcome"
          },
          {
            "id": "feat-season",
            "icon": "cal",
            "title": "Open Seasonally",
            "sub": "Apr 3 → Oct 18, 2026"
          }
        ],
        "badge": {
          "value": "4.1 ★",
          "label": "Google Rated",
          "sub": "Loved by our guests"
        }
      },
      "bookingBar": {
        "title": "Check availability",
        "sub": "We match you to the right site by hand — so availability always ends with a quick call.",
        "button": "Call to check availability",
        "buttonLink": {
          "action": "phone",
          "value": ""
        }
      },
      "stays": {
        "eyebrow": "Stay with us",
        "title": "Pick your *spot*.",
        "sub": "Three ways to stay, all with a fire ring, picnic table, and shade."
      },
      "amenities": {
        "photo": "/uploads/img-mq6332ej-77a4fb.webp",
        "badge": {
          "title": "Free Wi-Fi & Cable TV",
          "sub": "On every site"
        }
      },
      "gallery": {
        "title": "Life at *Butterfly*"
      },
      "activities": {
        "heading": "Fun for the *whole family*.",
        "button": "See all activities",
        "buttonLink": {
          "action": "page",
          "value": "activities"
        }
      },
      "journal": {
        "eyebrow": "From the journal",
        "title": "Featured stories"
      },
      "reviews": {
        "eyebrow": "Guest words",
        "title": "Loved by *families*.",
        "ratingValue": "4.1",
        "ratingCount": "321",
        "items": [
          {
            "id": "rev-choi",
            "name": "Choi Durante",
            "meta": "June 2026",
            "quote": "If you're looking for a peaceful, quiet, and relaxing camping trip with fun activities and a nice pool for the kids, this is definitely the place to go. All of the employees were amazing — especially Melissa, Don, and Lexi.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2taWlJYa3RURmhuUTI1VFQxOTBUVUpRZEZoWFgyYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2taWlJYa3RURmhuUTI1VFQxOTBUVUpRZEZoWFgyYxAB?hl=en"
          },
          {
            "id": "rev-susan",
            "name": "Susan Hopper",
            "meta": "June 2026",
            "quote": "The owners, Don and Melissa were absolutely wonderful and their daughter Angie went above and beyond to help us with our reservation and check in. The entire area was immaculate inside and out.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21WVGJVaEdjR0l5UVVJMVR6Y3laR3hIV1VkRlRXYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21WVGJVaEdjR0l5UVVJMVR6Y3laR3hIV1VkRlRXYxAB?hl=en"
          },
          {
            "id": "rev-david",
            "name": "David Ahrens",
            "meta": "June 2026",
            "quote": "We have stayed here a few times in the past and always come back. The owners are obviously improving the property and the location is convenient to many area hot spots.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21oQmIzSlhTM28yZEhCR2EwdDNUMlo0VGxabVZHYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21oQmIzSlhTM28yZEhCR2EwdDNUMlo0VGxabVZHYxAB?hl=en"
          },
          {
            "id": "rev-michelle",
            "name": "Michelle Dutton",
            "meta": "May 2026",
            "quote": "Family owned and operated! Extremely friendly people, quiet, very clean, from the little shop down to the public bathrooms!",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT25SblVXUkNUbk5GVTJWSWJYQjNVVU52TFUxNlVFRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT25SblVXUkNUbk5GVTJWSWJYQjNVVU52TFUxNlVFRRAB?hl=en"
          },
          {
            "id": "rev-tara",
            "name": "Tara Demiceli",
            "meta": "May 2026",
            "quote": "Love Butterfly campground! We have been going for several years. Warm friendly atmosphere. The family that owns this establishment are wonderful!",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21GRFkwaGxWV2hPY1hKQmQzQjRObTVKV2w5aE9FRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21GRFkwaGxWV2hPY1hKQmQzQjRObTVKV2w5aE9FRRAB?hl=en"
          },
          {
            "id": "rev-dawn",
            "name": "Dawn White",
            "meta": "June 2026",
            "quote": "Close to many central Jersey attractions and close to the shore. The Letho family genuinely care about their seasonals and all who camp here.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2kxTU1sOUNXbTUzYTNKSU0zVjBlRVUzYWtNeWEwRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2kxTU1sOUNXbTUzYTNKSU0zVjBlRVUzYWtNeWEwRRAB?hl=en"
          },
          {
            "id": "rev-donna",
            "name": "Donna A",
            "meta": "June 2026",
            "quote": "This is a small, family owned, family friendly campground. It has nice, clean, roomy lots. Even the seasonal lots are kept neat. The staff was very friendly and helpful.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21Sd2IyRklUbTVVTWxwaFNEVlVkM2hWY2pKV1ZtYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21Sd2IyRklUbTVVTWxwaFNEVlVkM2hWY2pKV1ZtYxAB?hl=en"
          },
          {
            "id": "rev-alissa",
            "name": "Alissa Walters",
            "meta": "September 2025",
            "quote": "To say this place is amazing would be an understatement. From the grounds to the family activities we had fun from the time we came to the time we left.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21kSk1GSXpZMVZEVDFCQ1JDMXRSSE5UU1U1alRYYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21kSk1GSXpZMVZEVDFCQ1JDMXRSSE5UU1U1alRYYxAB?hl=en"
          },
          {
            "id": "rev-courtney",
            "name": "Courtney Tarpy",
            "meta": "2025",
            "quote": "If you are looking for a campground with genuine owners, this is for you! My husband and I were camping under a unique situation and everyone could not have been more accommodating!",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2xBd1NWOTJUVVJJWWxSeVNVZFdTV05ZT0hWeWIxRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2xBd1NWOTJUVVJJWWxSeVNVZFdTV05ZT0hWeWIxRRAB?hl=en"
          },
          {
            "id": "rev-genairo",
            "name": "Genairo Zapata",
            "meta": "September 2025",
            "quote": "The staff is fantastic helpful and friendly the sites are clean the bathrooms and showers are super clean the seasonals are nice and respectful we love this place 100 stars if I could",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2tWWWVYaHpUVWxPY25WVVMwbzFNRWw0VkRKaE9IYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2tWWWVYaHpUVWxPY25WVVMwbzFNRWw0VkRKaE9IYxAB?hl=en"
          },
          {
            "id": "rev-rebecca",
            "name": "Rebecca Pilotte",
            "meta": "2025",
            "quote": "The bathrooms! I have never seen cleaner bathrooms and showers at a park or campground. Even my son was impressed by how clean the showers were.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSUQ3MTdXeDd3RRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSUQ3MTdXeDd3RRAB?hl=en"
          },
          {
            "id": "rev-ian",
            "name": "Ian Ian",
            "meta": "2025",
            "quote": "The cleanest park I have ever seen! All the campers that stay there are super welcoming and their kids played with ours the entire weekend. Saturday was filled with activities for the kids. Definitely going back here!",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSUNiNVBiU3JnRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSUNiNVBiU3JnRRAB?hl=en"
          },
          {
            "id": "rev-lenny",
            "name": "Lenny Goldberg",
            "meta": "September 2025",
            "quote": "The cabin was clean and nicely set up. The grounds and amenities are well kept.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2xnMllsRnhjMTlaTjFaQmQzaDFlVEl3ZFVvM2MzYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2xnMllsRnhjMTlaTjFaQmQzaDFlVEl3ZFVvM2MzYxAB?hl=en"
          },
          {
            "id": "rev-adam",
            "name": "Adam",
            "meta": "2025",
            "quote": "This campground has a very large, beautiful, clean swimming pool, dunk tank at special times, Karaoke, arcade, wagon rides, and many more activities. Good sized sites. You don't feel like you're on top of each other.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSUNyMllTaG5RRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSUNyMllTaG5RRRAB?hl=en"
          },
          {
            "id": "rev-shelby",
            "name": "Shelby Pape",
            "meta": "2024",
            "quote": "We had two adults and two kids in a rustic cabin. It met all expectations. There was AC, two windows, picnic bench outside, fire pit and space for a pack n play in the cabin.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChZDSUhNMG9nS0VJQ0FnSURwMnNQcVlnEAE!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChZDSUhNMG9nS0VJQ0FnSURwMnNQcVlnEAE?hl=en"
          },
          {
            "id": "rev-gail",
            "name": "Gail Feaver",
            "meta": "2024",
            "quote": "This was our first visit to Butterfly Camping Resort, and it will not be our last. The campsites are very shady and well maintained.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSURKaFlqMnV3RRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSURKaFlqMnV3RRAB?hl=en"
          },
          {
            "id": "rev-kimberly",
            "name": "Kimberly Nardone",
            "meta": "2022",
            "quote": "The family who owns it are SUPER nice, helpful people who go out of their way to make your camping experience a great one. Saturday activities for the kids and family were very fun and well run.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChZDSUhNMG9nS0VJQ0FnSUNhNF9pQ0t3EAE!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChZDSUhNMG9nS0VJQ0FnSUNhNF9pQ0t3EAE?hl=en"
          },
          {
            "id": "rev-ria",
            "name": "Ria Mungal",
            "meta": "2024",
            "quote": "Rented a cabin for 3 nights. It was really clean and comfortable. This is a great place for a family trip. Loved the saltwater pool, it was very clean.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSURKOXNXMTFnRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSURKOXNXMTFnRRAB?hl=en"
          },
          {
            "id": "rev-kathleen",
            "name": "Kathleen Popola",
            "meta": "2023",
            "quote": "This is a great campground with plenty of sites, all nestled inside the woods. We parked our 42' fifth wheel without any problems.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSURlbzV2YXFRRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSURlbzV2YXFRRRAB?hl=en"
          }
        ],
        "pullQuote": {
          "text": "The kind of place you measure summers by — our kids count down to it all year.",
          "attribution": "— A returning Butterfly family"
        }
      },
      "nearby": {
        "eyebrow": "Beyond the grounds",
        "title": "Explore *nearby*.",
        "sub": "Big days out or quiet ones — there's plenty within easy reach of the campground.",
        "items": [
          {
            "id": "near-sixflags",
            "distance": "~5 miles",
            "title": "Six Flags Great Adventure",
            "desc": "Coasters, a drive-through safari, and the water park — minutes from your site."
          },
          {
            "id": "near-shore",
            "distance": "~45 minutes",
            "title": "The Jersey Shore",
            "desc": "Point Pleasant, Seaside, and the boardwalks make an easy day trip east."
          },
          {
            "id": "near-bogs",
            "distance": "Adjacent",
            "title": "Butterfly Bogs & Trails",
            "desc": "Pine Barrens cranberry bogs and quiet hiking right next to the grounds."
          }
        ]
      },
      "location": {
        "eyebrow": "Find us",
        "title": "360 Butterfly Rd. Jackson, NJ 08527",
        "ctaEyebrow": "Ready to book?",
        "ctaTitle": "Give us a ring."
      },
      "finalCta": {
        "eyebrow": "Your weekend's waiting",
        "title": "Ready to *escape*?",
        "sub": "Pick a weekend, grab the family, and let us match you to the right site — it starts with a call.",
        "secondaryLabel": "View accommodations",
        "primaryLink": {
          "action": "phone",
          "value": ""
        },
        "secondaryLink": {
          "action": "page",
          "value": "rv"
        }
      }
    },
    "contactCtas": {
      "eventsCallLink": {
        "action": "phone",
        "value": ""
      },
      "blogCallLink": {
        "action": "phone",
        "value": ""
      }
    },
    "announcement": {
      "on": false,
      "notices": [
        {
          "id": "promo",
          "type": "Promotion / Limited Offer",
          "on": true,
          "icon": "gift",
          "theme": "amber",
          "text": "Special offer: 50% off this weekend!",
          "link": ""
        },
        {
          "id": "maintenance",
          "type": "Maintenance",
          "on": true,
          "icon": "alert",
          "theme": "amber",
          "text": "Scheduled maintenance Sunday 2–4 AM.",
          "link": ""
        },
        {
          "id": "coming",
          "type": "Coming Soon",
          "on": true,
          "icon": "star",
          "theme": "purple",
          "text": "New cabins launching next season!",
          "link": ""
        },
        {
          "id": "tips",
          "type": "Tips / Information",
          "on": true,
          "icon": "info",
          "theme": "blue",
          "text": "Reserve by phone — we still answer it ourselves.",
          "link": ""
        }
      ],
      "title": "Site under construction.",
      "text": "Some photos and event listings are placeholders for the 2026 season.",
      "link": "#rv"
    },
    "contact": {
      "phone": "(732) 928-2107",
      "email": "butterflycamp2107@gmail.com",
      "addr1": "360 Butterfly Road",
      "addr2": "Jackson, New Jersey 08527"
    },
    "officeHours": [
      [
        "Mon – Thu",
        "10am – 5pm"
      ],
      [
        "Friday",
        "10am – 7pm"
      ],
      [
        "Saturday",
        "9am – 6pm"
      ],
      [
        "Sunday",
        "9am – 5pm"
      ]
    ],
    "season": {
      "opens": "April 3, 2026",
      "closes": "October 18, 2026",
      "chip": "Apr 3 → Oct 18, 2026",
      "ribbon": true
    },
    "nav": {
      "rv": true,
      "tent": true,
      "cabins": true,
      "activities": true,
      "events": true,
      "gallery": true,
      "blog": true,
      "rules": true
    },
    "grounds": {
      "source": "selected"
    },
    "photos": [
      {
        "id": "p-mq4ldf30-4qn0",
        "label": "206427_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldf2y-c750c9.jpg",
        "featured": true
      },
      {
        "id": "p-mq4ldf3m-3tn1",
        "label": "8559582_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldf3j-60269b.jpg"
      },
      {
        "id": "p-mq4ldf5t-68ib",
        "label": "37284631_1307201122747121_8943035037498998784_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldf5r-966914.webp"
      },
      {
        "id": "p-mq4ldf7d-4kzv",
        "label": "37348086_1307201232747110_4135658291747356672_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldf7b-1fe8ae.webp"
      },
      {
        "id": "p-mq4ldf7z-k3qs",
        "label": "71347876_1662715747195655_3487907108169449472_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldf7x-8b4a4b.jpg"
      },
      {
        "id": "p-mq4ldf8k-gjik",
        "label": "120463685_2032717070195519_4853004308705120239_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldf8j-52e1cf.webp"
      },
      {
        "id": "p-mq4ldf95-l9wj",
        "label": "120563119_2032717130195513_8644085534831044377_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldf92-2e7d52.jpg"
      },
      {
        "id": "p-mq4ldf9r-aoj0",
        "label": "120576376_2032716920195534_3359679622234419108_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldf9p-fe1b10.webp"
      },
      {
        "id": "p-mq4ldfa8-417b",
        "label": "121049934_2036251573175402_8520115905312594536_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfa6-92226a.jpg",
        "featured": true
      },
      {
        "id": "p-mq4ldfat-8w3m",
        "label": "121059710_2036250179842208_4380088001972750914_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfaq-294312.jpg",
        "featured": true
      },
      {
        "id": "p-mq4ldfby-eccu",
        "label": "123444174_2067091543424738_6810121120571652891_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfbx-9eef70.jpg"
      },
      {
        "id": "p-mq4ldfci-jd2x",
        "label": "123494765_2067091633424729_6157355270962446957_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfcg-8dba95.jpg"
      },
      {
        "id": "p-mq4ldfd5-duol",
        "label": "176405837_2210185782448646_3885849264143929972_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfd2-c353b4.jpg"
      },
      {
        "id": "p-mq4ldfdo-99uv",
        "label": "193546643_2243729612427596_673764417716350323_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfdm-53ec92.jpg"
      },
      {
        "id": "p-mq4ldfe9-iy17",
        "label": "194355413_2243728165761074_9206585976713276651_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfe7-adf902.jpg"
      },
      {
        "id": "p-mq4ldfet-6pvv",
        "label": "198324641_2248315691968988_7435536612868655887_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfer-7acab3.jpg"
      },
      {
        "id": "p-mq4ldffc-e0bb",
        "label": "201111520_2254571951343362_9020352239246202071_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldffa-333490.jpg"
      },
      {
        "id": "p-mq4ldffv-evas",
        "label": "201869948_2254571914676699_5835303640925771531_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldffs-1be159.jpg"
      },
      {
        "id": "p-mq4ldfgi-gx9i",
        "label": "240521883_2310186712448552_5377579882482305905_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfgg-6f2046.jpg"
      },
      {
        "id": "p-mq4ldfgz-heu7",
        "label": "284956271-472992664625790-1409219409785015462-n_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfgx-bce326.webp"
      },
      {
        "id": "p-mq4ldfhf-imie",
        "label": "285542517-472992597959130-6466469556128503675-n_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfhe-e1e1e2.webp"
      },
      {
        "id": "p-mq4ldfi2-gt0m",
        "label": "287976758-480668640524859-6012100931738636379-n_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfi0-ddc7e5.webp"
      },
      {
        "id": "p-mq4ldfis-9so8",
        "label": "469454401_3321619147971965_4337735733397376216_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfiq-b3e52f.webp"
      },
      {
        "id": "p-mq4ldfjg-5wv0",
        "label": "469481552_3321618557972024_954499366198512719_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfje-c2f8ff.webp"
      },
      {
        "id": "p-mq4ldfk2-9xh4",
        "label": "469522101_3321619137971966_2990622450417412321_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfk0-e4990d.webp"
      },
      {
        "id": "p-mq4ldfkm-c7sd",
        "label": "469549233_3321619061305307_5009136914273330164_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfkk-245d11.jpg"
      },
      {
        "id": "p-mq4ldfl3-3e79",
        "label": "469620355_3321619111305302_6615114107729737289_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfl2-d3d176.jpg"
      },
      {
        "id": "p-mq4ldfln-e16i",
        "label": "469693786_3321619211305292_4686211810888255760_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldflk-d483e1.jpg"
      },
      {
        "id": "p-mq4ldfm4-6cuj",
        "label": "469696563_3321619027971977_6841536065532446598_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfm2-5aa300.webp"
      },
      {
        "id": "p-mq4ldfmr-gnrf",
        "label": "469796650_3321619041305309_6862483236478613567_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfmp-b9efc5.webp"
      },
      {
        "id": "p-mq4ldfnq-j41w",
        "label": "469860875_3322488771218336_4346988641439709685_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfnn-00db9b.webp"
      },
      {
        "id": "p-mq4ldfo6-jjs9",
        "label": "472486347_3344495595684320_4338912952852015819_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfo5-0ce351.jpg"
      },
      {
        "id": "p-mq4ldfol-gq5m",
        "label": "472538787_3344495335684346_3829182108260882887_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfok-00b3d0.jpg"
      },
      {
        "id": "p-mq4ldfp1-g1cl",
        "label": "473190980_3350850698382143_5966649586455278263_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfp0-79aee1.jpg"
      },
      {
        "id": "p-mq4ldfpi-bqk",
        "label": "473416099_3350850578382155_6686132131424124838_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfpg-fe179f.jpg"
      },
      {
        "id": "p-mq4ldfr2-gw9w",
        "label": "474466497_1080798730511844_2670621976722771953_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfr0-d11d77.webp"
      },
      {
        "id": "p-mq4ldfrg-hpll",
        "label": "475826379_3368193086647904_7269652671904524913_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfrf-1ba437.jpg"
      },
      {
        "id": "p-mq4ldfru-iwoy",
        "label": "475858978_3368193309981215_8686283121678071160_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfrq-f0541f.jpg"
      },
      {
        "id": "p-mq4ldftg-c2zb",
        "label": "476516401_1094255632499487_3175711654845507294_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfte-51bce9.webp"
      },
      {
        "id": "p-mq4ldfuc-mew",
        "label": "477053131_1093624085895975_7440488653326342888_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfua-bdf5a1.webp"
      },
      {
        "id": "p-mq4ldfuy-ki9e",
        "label": "479689604_1098046175453766_5612878730663005661_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfuw-9ccea2.webp"
      },
      {
        "id": "p-mq4ldfvi-e24w",
        "label": "506348667_3519775108156367_2681634249834260736_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfvg-75a141.webp"
      },
      {
        "id": "p-mq4ldfwn-9tjg",
        "label": "506594206_3527091157424762_6782611669812158981_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfwk-17de63.webp"
      },
      {
        "id": "p-mq4ldfy6-2irm",
        "label": "506705247_3527091064091438_99511316606246423_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfy4-a74e5d.webp"
      },
      {
        "id": "p-mq4ldfzq-i5ia",
        "label": "506720607_3527091267424751_4540677099410956251_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldfzo-537709.webp"
      },
      {
        "id": "p-mq4ldg15-4r6a",
        "label": "506784819_3527091060758105_3278669728312308094_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg13-4f39ba.webp"
      },
      {
        "id": "p-mq4ldg2q-ax4u",
        "label": "508224805_3527091110758100_8574189531025430739_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg2n-ea2489.webp"
      },
      {
        "id": "p-mq4ldg39-63w2",
        "label": "509430556_3534764466657431_4527127439075083684_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg36-a28a5a.jpg"
      },
      {
        "id": "p-mq4ldg3q-a29n",
        "label": "659084863_1502603631211139_2037208112235801547_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg3o-e1d1c9.jpg"
      },
      {
        "id": "p-mq4ldg52-ciwl",
        "label": "659726524_122093822702782759_7926061874737511518_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg50-2e05e5.webp"
      },
      {
        "id": "p-mq4ldg66-i0qe",
        "label": "660168383_122093992406782759_5655173350540795202_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg64-37bcd6.webp"
      },
      {
        "id": "p-mq4ldg6s-kznu",
        "label": "660883761_10164775736039923_3445664835455961843_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg6q-37a8ba.webp"
      },
      {
        "id": "p-mq4ldg7m-fewb",
        "label": "dogs_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg7k-0c69db.webp"
      },
      {
        "id": "p-mq4ldg8g-138a",
        "label": "dogs-2_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg8e-88c81f.webp"
      },
      {
        "id": "p-mq4ldg8x-8584",
        "label": "dogs-3_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg8v-713feb.jpg"
      },
      {
        "id": "p-mq4ldg9o-975",
        "label": "dsc-4998_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldg9m-77ba78.jpg"
      },
      {
        "id": "p-mq4ldga9-ddh8",
        "label": "pop-campground_orig",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq4ldga7-4d419e.jpg"
      }
    ],
    "blog": [],
    "events": [
      {
        "m": "April",
        "sum": "Open weekend · Easter egg hunt · spring cleanup",
        "badge": "",
        "weeks": [
          {
            "id": "apr1",
            "title": "Opening Weekend!",
            "badge": "BADGE",
            "on": true
          },
          {
            "id": "apr2",
            "title": "Easter Egg Hunt",
            "badge": "",
            "on": true
          },
          {
            "id": "apr3",
            "title": "Astronomy Night",
            "badge": "",
            "on": true
          },
          {
            "id": "apr4",
            "title": "Campfire Social",
            "badge": "",
            "on": true
          }
        ]
      },
      {
        "m": "May",
        "sum": "Pool opening · Mother's Day brunch · Memorial Day",
        "badge": "Pool opens",
        "weeks": [
          {
            "id": "may1",
            "title": "Pool Opening",
            "badge": "Pool opens",
            "on": true
          },
          {
            "id": "may2",
            "title": "Mother's Day Brunch",
            "badge": "",
            "on": true
          },
          {
            "id": "may3",
            "title": "Tie-Dye Day",
            "badge": "",
            "on": true
          },
          {
            "id": "may4",
            "title": "Memorial Day BBQ",
            "badge": "Holiday",
            "on": true
          }
        ]
      },
      {
        "m": "June",
        "sum": "Tie-dye festival · Father's Day · summer kickoff",
        "badge": "",
        "weeks": [
          {
            "id": "jun1",
            "title": "Summer Kickoff",
            "badge": "",
            "on": true
          },
          {
            "id": "jun2",
            "title": "Father's Day",
            "badge": "",
            "on": true
          },
          {
            "id": "jun3",
            "title": "Glow Dance",
            "badge": "",
            "on": true
          },
          {
            "id": "jun4",
            "title": "Pavilion Music",
            "badge": "",
            "on": true
          }
        ]
      },
      {
        "m": "July",
        "sum": "Fourth of July · Christmas in July · family olympics",
        "badge": "Fourth of July",
        "weeks": [
          {
            "id": "jul1",
            "title": "Independence Day",
            "badge": "Fourth of July",
            "on": true
          },
          {
            "id": "jul2",
            "title": "Christmas in July",
            "badge": "",
            "on": true
          },
          {
            "id": "jul3",
            "title": "Family Olympics",
            "badge": "",
            "on": true
          },
          {
            "id": "jul4",
            "title": "Ice Cream Social",
            "badge": "",
            "on": true
          }
        ]
      },
      {
        "m": "August",
        "sum": "Pirate week · Hawaiian luau · end-of-summer bash",
        "badge": "",
        "weeks": [
          {
            "id": "aug1",
            "title": "Pirate Week",
            "badge": "",
            "on": true
          },
          {
            "id": "aug2",
            "title": "Hawaiian Luau",
            "badge": "",
            "on": true
          },
          {
            "id": "aug3",
            "title": "Talent Show",
            "badge": "",
            "on": true
          },
          {
            "id": "aug4",
            "title": "Summer's-End Bash",
            "badge": "",
            "on": true
          }
        ]
      },
      {
        "m": "September",
        "sum": "Labor Day · harvest weekend · fall colors",
        "badge": "",
        "weeks": [
          {
            "id": "sep1",
            "title": "Labor Day",
            "badge": "",
            "on": true
          },
          {
            "id": "sep2",
            "title": "Harvest Weekend",
            "badge": "",
            "on": true
          },
          {
            "id": "sep3",
            "title": "Chili Cook-off",
            "badge": "",
            "on": true
          }
        ]
      },
      {
        "m": "October",
        "sum": "Halloween extravaganza · closing weekend",
        "badge": "Halloween",
        "weeks": [
          {
            "id": "oct1",
            "title": "Trunk-or-Treat",
            "badge": "",
            "on": true
          },
          {
            "id": "oct2",
            "title": "Costume Parade",
            "badge": "",
            "on": true
          },
          {
            "id": "oct3",
            "title": "Haunted Trail",
            "badge": "Halloween",
            "on": true
          },
          {
            "id": "oct4",
            "title": "Closing Weekend",
            "badge": "",
            "on": true
          }
        ]
      }
    ],
    "pages": {
      "rv": {
        "card": {
          "t": "RV Sites",
          "sub": "128 full-hookup sites",
          "src": "/uploads/img-mq6wn64a-352464.webp"
        },
        "hero": {
          "eyebrow": "Accommodations · RV",
          "title": "128 full-hookup sites.",
          "sub": "30-amp and 50-amp options, with a fire ring, picnic table, and pine shade on every single site.",
          "stats": [
            [
              "128",
              "Sites"
            ],
            [
              "2",
              "Hookup tiers"
            ],
            [
              "30/50",
              "Amp options"
            ]
          ],
          "photo": "/uploads/img-mq6w4xpm-36a696.webp"
        },
        "pricing": {
          "eyebrow": "Pick your hookup",
          "title": "Pricing, per night.",
          "tiers": [
            {
              "id": "we",
              "pull": { "price": 0, "weekly": false, "weeklyPrice": 0, "monthly": false, "monthlyPrice": 0, "seasonal": false, "seasonalPrice": 0 },
              "t": "Water & Electric",
              "price": 74,
              "popular": false,
              "feats": [
                "Water hookup",
                "Electric service",
                "Fire ring",
                "Picnic table",
                "Pine shade"
              ],
              "monthly": true,
              "weekly": true,
              "weeklyPrice": 468,
              "monthlyPrice": 1064,
              "seasonalPrice": 0
            },
            {
              "id": "30a",
              "pull": { "price": 0, "weekly": false, "weeklyPrice": 0, "monthly": false, "monthlyPrice": 0, "seasonal": false, "seasonalPrice": 0 },
              "t": "30 Amp Full Hookup",
              "price": 85,
              "popular": false,
              "feats": [
                "Water, sewer, electric",
                "30-amp service",
                "Fire ring & table",
                "Pull-thru available",
                "Wifi included"
              ],
              "weekly": true,
              "weeklyPrice": 545,
              "monthlyPrice": 1284,
              "seasonalPrice": 0,
              "monthly": true
            },
            {
              "id": "50a",
              "pull": { "price": 0, "weekly": false, "weeklyPrice": 0, "monthly": false, "monthlyPrice": 0, "seasonal": false, "seasonalPrice": 0 },
              "t": "50 Amp Full Hookup",
              "price": 93,
              "popular": false,
              "feats": [
                "Water, sewer, electric",
                "50-amp service",
                "Larger gravel pad",
                "Premium location",
                "Pull-thru standard"
              ],
              "weekly": true,
              "weeklyPrice": 601,
              "monthlyPrice": 1484,
              "seasonalPrice": 0,
              "monthly": true
            }
          ]
        },
        "amenities": {
          "eyebrow": "What's on every site",
          "title": "Everything you need.",
          "items": [
            "Fire ring",
            "Picnic table",
            "Pine shade",
            "Trash pickup",
            "Free wifi",
            "Bathhouse access",
            "Laundry room",
            "Camp store"
          ]
        },
        "reserveLink": {
          "action": "phone",
          "value": ""
        },
        "cta": {
          "title": "Ready to roll in?",
          "sub": "Call the office — we'll match your rig to the right site.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          },
          "secondaryLink": {
            "action": "email",
            "value": ""
          }
        }
      },
      "tent": {
        "card": {
          "t": "Tent Sites",
          "sub": "6 quiet wooded sites",
          "src": "/uploads/img-mq6x1std-4d8e4c.webp"
        },
        "hero": {
          "eyebrow": "Accommodations · Tent",
          "title": "6 quiet wooded sites.",
          "sub": "Tucked back from the RV loops, under a full pine canopy. Fire ring and picnic table on each.",
          "photo": "/uploads/img-mq75tw52-84954a.webp"
        },
        "intro": {
          "eyebrow": "The basics",
          "title": "Sleep on the ground, the old way.",
          "body": "Six wooded sites set well apart from the main loops. More quiet, more shade, and a proper sense of wilderness — even this close to the Jersey shore.",
          "price": 40,
          "tone": "fog",
          "photoLabel": "Tent site under pines",
          "src": "/uploads/img-mq6rsgy2-f501db.webp"
        },
        "features": [
          {
            "id": "f1",
            "title": "Fire ring & picnic table",
            "body": "Bring marshmallows and a folding chair — the rest is already waiting at your site.",
            "tone": "pine",
            "src": "/uploads/img-mq77x8rh-1bb02e.webp"
          },
          {
            "id": "f2",
            "title": "Bathhouse close by",
            "body": "Hot showers, flush toilets, and we keep it genuinely tidy. No long walks in the dark.",
            "tone": "water",
            "src": "/uploads/img-mq76dp0o-77435d.webp"
          },
          {
            "id": "f3",
            "title": "A real shaded canopy",
            "body": "Our trees shade keeps your site cool through the day, even in the thick of July.",
            "tone": "meadow",
            "src": "/uploads/img-mq76e1xa-8926eb.webp"
          }
        ],
        "reserveLink": {
          "action": "phone",
          "value": ""
        },
        "cta": {
          "title": "Want one of the six?",
          "sub": "They go fast — call early in the season to lock in your weekend.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          },
          "secondaryLink": {
            "action": "email",
            "value": ""
          }
        }
      },
      "cabins": {
        "card": {
          "t": "Cabins",
          "sub": "8 cozy cabins · sleeps 4–6",
          "src": ""
        },
        "hero": {
          "eyebrow": "Accommodations · Cabins",
          "title": "8 cozy cabins.",
          "sub": "Sleeps four to six. A/C, mini-fridge, and microwave inside — just bring your own linens.",
          "stats": [
            [
              "8",
              "Cabins"
            ],
            [
              "4–6",
              "Sleeps"
            ],
            [
              "$128.58 - $207.82",
              "From / night"
            ]
          ],
          "photo": ""
        },
        "units": [
          {
            "id": "cab-bear",
            "mode": "cards",
            "eyebrow": "The Cabins",
            "name": "Bear Bone Cabin",
            "price": 128.58,
            "blurb": "Our classic camp cabin — bunks for the kids, a queen for you, and a porch built for slow mornings.",
            "photos": [
              {
                "tone": "dusk",
                "label": "Bear Bone exterior at dusk",
                "src": "/uploads/img-mq6pro42-18b2d9.webp"
              },
              {
                "tone": "neutral",
                "label": "Bear Bone interior",
                "src": "/uploads/img-mq6pryut-143a71.webp"
              },
              {
                "tone": "pine",
                "label": "Shaded outdoor area",
                "src": "/uploads/img-mq783bpd-c43350.webp"
              }
            ],
            "specs": [
              [
                "Queen bed",
                "plus two bunk beds"
              ],
              [
                "A/C + heat",
                "climate controlled"
              ],
              [
                "Mini-fridge",
                "plus microwave"
              ],
              [
                "Covered porch",
                "without seating"
              ],
              [
                "Fire ring",
                "wood sold at office"
              ],
              [
                "Picnic table",
                "right out front"
              ]
            ],
            "tags": []
          },
          {
            "id": "cab-deluxe",
            "mode": "cards",
            "eyebrow": "The Cabins",
            "name": "Deluxe Cabin",
            "price": 154.5,
            "blurb": "More room, more comfort. A full kitchenette, private bath, and a wraparound deck under the pines.",
            "photos": [
              {
                "tone": "water",
                "label": "Deluxe exterior",
                "src": "/uploads/img-mq6ptdvt-b339d6.webp"
              },
              {
                "tone": "meadow",
                "label": "Kitchenette & living space",
                "src": "/uploads/img-mq6ptrbw-cb9ff3.webp"
              },
              {
                "tone": "fog",
                "label": "Outdoor area",
                "src": "/uploads/img-mq78462i-849715.webp"
              }
            ],
            "specs": [
              [
                "Queen + bunks",
                "sleeps up to six"
              ],
              [
                "Full kitchenette",
                "fridge, stove & microwave"
              ],
              [
                "Private bath",
                "shower & toilet inside"
              ],
              [
                "A/C + heat",
                "climate controlled"
              ],
              [
                "Wraparound deck",
                "under the pines"
              ],
              [
                "Fire ring & table",
                "right out front"
              ]
            ],
            "tags": []
          },
          {
            "id": "cab-cottage",
            "mode": "tags",
            "eyebrow": "The Cabins",
            "name": "Cottage",
            "price": 207.82,
            "blurb": "Our largest stay — a proper little house for families, with a full kitchen, two bedrooms, and a yard of its own.",
            "photos": [
              {
                "tone": "dusk",
                "label": "Cottage exterior",
                "src": "/uploads/img-mq6pu1jw-395b3b.webp"
              },
              {
                "tone": "neutral",
                "label": "Full kitchen, dining & Living room",
                "src": "/uploads/img-mq6puaaj-cc218c.webp"
              },
              {
                "tone": "pine",
                "label": "Private Bedroom",
                "src": "/uploads/img-mq6pv3h4-504d00.webp"
              }
            ],
            "specs": [],
            "tags": [
              "Two bedrooms",
              "Sleeps up to eight",
              "Full kitchen",
              "Full bathroom",
              "Living & dining",
              "A/C + heat"
            ]
          }
        ],
        "bring": {
          "eyebrow": "You bring",
          "title": "The personal stuff.",
          "items": [
            "Sheets & pillows",
            "Towels",
            "Toiletries",
            "Cookware (if cooking)",
            "Cooler with ice"
          ]
        },
        "provide": {
          "eyebrow": "We provide",
          "title": "The rest.",
          "items": [
            "Bed, A/C & heat",
            "Mini-fridge & microwave",
            "Lighting & outlets",
            "Free wifi",
            "Trash pickup"
          ]
        },
        "reserveLink": {
          "action": "phone",
          "value": ""
        },
        "cta": {
          "title": "Pick a weekend.",
          "sub": "Eight cabins, one season — the good dates fill up by spring.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          },
          "secondaryLink": {
            "action": "email",
            "value": ""
          }
        }
      },
      "activities": {
        "hero": {
          "eyebrow": "Things to do!",
          "title": "22+ activities, all weekend long.!",
          "sub": "Sports, lawn games, indoor recreation, nature, family programs, and evenings by the fire.!",
          "stats": [
            [
              "22+",
              "Activities"
            ],
            [
              "4",
              "Outdoor courts"
            ],
            [
              "6",
              "Lawn games"
            ],
            [
              "Free",
              "For guests"
            ]
          ],
          "photo": ""
        },
        "items": [
          {
            "id": "act1",
            "title": "Outdoor Sports",
            "desc": "Tennis, basketball, pickleball, and sand volleyball — courts kept in great shape all season.",
            "tags": [
              "Tennis courts",
              "Basketball",
              "Pickleball",
              "Sand volleyball"
            ],
            "tone": "meadow",
            "src": "/uploads/img-mq7zdpcf-37ac84.webp"
          },
          {
            "id": "act2",
            "title": "Lawn Games",
            "desc": "Horseshoes, cornhole, ladder toss, and a giant Jenga set that somehow never stops getting used.",
            "tags": [
              "Horseshoes",
              "Cornhole",
              "Ladder toss",
              "Giant Jenga"
            ],
            "tone": "dusk",
            "src": "/uploads/img-mq7ze617-e42cb7.webp"
          },
          {
            "id": "act3",
            "title": "Indoor Recreation",
            "desc": "An arcade and game room for the in-between hours, plus movie nights whenever the rain rolls in.",
            "tags": [
              "Arcade",
              "Game room",
              "Movie nights",
              "Foosball"
            ],
            "tone": "night",
            "src": ""
          },
          {
            "id": "act4",
            "title": "Nature & Outdoors",
            "desc": "Hiking trails, guided nature walks, and a stocked fishing pond right on the property.",
            "tags": [
              "Hiking trails",
              "Bird watching",
              "Fishing pond",
              "Bog walk"
            ],
            "tone": "pine",
            "src": "/uploads/img-mq7zgook-520f81.webp"
          },
          {
            "id": "act5",
            "title": "Kids & Family",
            "desc": "A big playground, a splash pad for hot afternoons, and weekend programs the kids actually ask for.",
            "tags": [
              "Playground",
              "Splash pad",
              "Craft table",
              "Family bingo"
            ],
            "tone": "water",
            "src": "/uploads/img-mq7zfbu7-f5ba94.webp"
          },
          {
            "id": "act6",
            "title": "Evening & Social",
            "desc": "Campfires, live music in the pavilion, and ice-cream socials that run late into the night.",
            "tags": [
              "Campfires",
              "Live music",
              "Ice cream nights",
              "Karaoke"
            ],
            "tone": "dusk",
            "src": ""
          }
        ],
        "cta": {
          "title": "Curious what's running this weekend?",
          "sub": "The schedule shifts week to week — give the office a call.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          }
        }
      },
      "events": {
        "hero": {
          "eyebrow": "2026 Season",
          "title": "What's happening, weekend by weekend.",
          "sub": "From the pool opening in May to October's Halloween extravaganza — 28+ themed weekends.",
          "photo": ""
        }
      },
      "gallery": {
        "hero": {
          "eyebrow": "Photographs",
          "title": "Moments from the campground.",
          "sub": "Tap any image to open it full-screen, then use the arrows or your keyboard to move through.",
          "photo": ""
        }
      },
      "blog": {
        "hero": {
          "eyebrow": "Field notes",
          "title": "Stories from the campground.",
          "sub": "Tips, season recaps, and the occasional bear sighting — straight from the family.",
          "photo": ""
        }
      },
      "rules": {
        "hero": {
          "eyebrow": "Good to know",
          "title": "Rules & regulations.",
          "sub": "A few simple rules keep the campground safe, quiet, and fun for every family — thanks for helping us keep it that way.",
          "photo": ""
        },
        "sections": [
          {
            "id": "rul-checkin",
            "title": "Check-in & check-out",
            "items": [
              "Check-in starts at 3pm and check-out is by noon — call the office if you need flexibility.",
              "All campers and visitors must register at the office on arrival.",
              "Arriving late? Call ahead and we'll leave instructions for you."
            ]
          },
          {
            "id": "rul-quiet",
            "title": "Quiet hours",
            "items": [
              "Quiet hours are 11pm to 8am, every night.",
              "Music and voices should stay at your own site — sound carries in the pines.",
              "Generators are not permitted overnight."
            ]
          },
          {
            "id": "rul-fires",
            "title": "Campfires",
            "items": [
              "Fires belong in the fire rings only, and must never be left unattended.",
              "Put your fire completely out before turning in or leaving your site.",
              "Please don't bring firewood from out of the area — it can carry invasive pests. Firewood is available at the camp store."
            ]
          },
          {
            "id": "rul-pets",
            "title": "Pets",
            "items": [
              "Pets are welcome! Keep them leashed at all times and clean up after them.",
              "Pets can't be left unattended at your site, and aren't permitted in the buildings or pool area."
            ]
          },
          {
            "id": "rul-pool",
            "title": "Pool",
            "items": [
              "The pool is open to registered guests during posted hours.",
              "There is no lifeguard on duty — swim at your own risk.",
              "Children must be accompanied by an adult."
            ]
          },
          {
            "id": "rul-vehicles",
            "title": "Vehicles & golf carts",
            "items": [
              "The speed limit everywhere in the campground is 5 mph.",
              "Licensed drivers only — on the roads and on golf carts.",
              "One vehicle per site, please — extra vehicles park in the overflow lot."
            ]
          },
          {
            "id": "rul-visitors",
            "title": "Visitors",
            "items": [
              "Day visitors are welcome — they must register at the office and leave by quiet hours.",
              "Overnight guests must be registered; a small guest fee may apply."
            ]
          },
          {
            "id": "rul-site",
            "title": "Trash & your site",
            "items": [
              "Bag your trash and drop it at the dumpsters — please don't leave it out overnight.",
              "Leave your site the way you found it — no digging, nailing into trees, or moving fire rings."
            ]
          }
        ],
        "cta": {
          "title": "Questions about a rule?",
          "sub": "Call the office — we're happy to walk you through anything before you arrive.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          }
        }
      }
    },
    "seo": {
      "site": {
        "siteName": "Butterfly Camping Resort",
        "baseUrl": "https://butterflycamping.com",
        "titleTemplate": "%s · Butterfly Camping Resort",
        "defaultDescription": "Family-owned camping resort in the New Jersey Pine Barrens — 128 full-hookup RV sites, 6 wooded tent sites, and 8 cabins on 200 quiet acres. Open April through October.",
        "defaultShareImage": "",
        "twitterHandle": "",
        "favicon": "/uploads/img-mq6htcl1-bc8562-icon.png",
        "business": {
          "type": "Campground",
          "name": "Butterfly Camping Resort",
          "streetAddress": "360 Butterfly Road",
          "addressLocality": "Jackson",
          "addressRegion": "NJ",
          "postalCode": "08527",
          "country": "US",
          "telephone": "732-928-2107",
          "priceRange": "$$",
          "latitude": "",
          "longitude": "",
          "sameAs": "https://www.facebook.com/ButterflyCampingResort/\nhttps://www.instagram.com/butterfly.camping.resort/\nhttps://www.google.com/maps?cid=10377840934788359529\nhttps://www.tripadvisor.com/Hotel_Review-g46528-d1103469-Reviews-Butterfly_Camping_Resort-Jackson_New_Jersey.html\nhttps://www.yelp.com/biz/butterfly-camping-resort-jackson\nhttps://www.goodsam.com/campgrounds-rv-parks/new-jersey/jackson/butterfly-camping-resort/cgid-740000864\nhttps://campnj.com/campgrounds/butterfly-camping-resort\nhttps://thedyrt.com/camping/new-jersey/butterfly-camping-resort"
        }
      },
      "pages": {
        "home": {
          "title": "",
          "description": "",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "rv": {
          "title": "RV Sites",
          "description": "128 full-hookup RV sites with 30- and 50-amp service, water, and sewer — set in shady NJ pine.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "tent": {
          "title": "Tent Sites",
          "description": "6 quiet, wooded tent sites with fire rings and picnic tables, tucked into the Pine Barrens.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "cabins": {
          "title": "Cabins",
          "description": "8 cozy cabins for a roof-over-your-head stay in the woods — perfect for first-time campers.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "activities": {
          "title": "Activities",
          "description": "Trails, the spring-fed pond, fishing, and family events all season long at Butterfly Camp.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "events": {
          "title": "Events",
          "description": "See what's on this season — opening weekends, holiday cookouts, and family activities.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "gallery": {
          "title": "Photo Gallery",
          "description": "Photos from around the campground — the pines, the pond, campfires, and happy campers.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "blog": {
          "title": "Field Notes",
          "description": "Tips, season recaps, and stories from the family that runs Butterfly Camping Resort.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "rules": {
          "title": "Rules & Regulations",
          "description": "Campground rules at Butterfly Camping Resort — quiet hours, campfires, pets, pool, vehicles, and visitors.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        }
      }
    }
  };

  // ── BAKED — a full snapshot of the live content.json ───────────────────────
  // Generated by `npm run bake`; do not hand-edit. Overlaid on DEFAULTS (see
  // EFFECTIVE_DEFAULTS) so everything the admin edited on the live site survives
  // a fresh deploy and "Reset to defaults". Empty until the first bake.
  // BAKE:CONTENT:START
  const BAKED = {
    "version": 1,
    "hero": {
      "badge": "Est. 1997 - Family Owned & Operated Campground",
      "headline": "Where Families\nFind Their\nWild Side",
      "sub": "15 acres of shaded campsites, cozy cabins, and outdoor adventure in the heart of New Jersey. Open April 3rd through October 18th.",
      "cta": "See accommodations",
      "ctaPrimary": "Book by phone",
      "photo": "/uploads/img-mq5zplpf-008dc6.webp",
      "ctaPrimaryLink": {
        "action": "phone",
        "value": ""
      },
      "ctaLink": {
        "action": "page",
        "value": "rv"
      }
    },
    "brand": {
      "name": "Butterfly Camping",
      "sub": "Resort Est. 1997 · Jackson NJ",
      "logo": "/uploads/img-mq2nt827-b0e95f.webp"
    },
    "footer": {
      "tagline": "15 acres of shaded campsites, cozy cabins, and outdoor adventure in the heart of New Jersey. Open April 3rd through October 18th."
    },
    "home": {
      "about": {
        "eyebrow": "About the resort",
        "title": "A quiet stretch\nof NJ pine.",
        "body": "Butterfly Camping Resort is a peaceful retreat located in Jackson Township, New Jersey, offering a relaxing outdoor getaway for families, campers, and travelers. Surrounded by nature, the resort provides spacious campsites, RV accommodations, and a welcoming environment for weekend trips or extended stays.",
        "stats": [
          [
            "128",
            "RV sites"
          ],
          [
            "6",
            "Tent sites"
          ],
          [
            "8",
            "Cabins"
          ]
        ],
        "photos": [
          {
            "tone": "pine",
            "label": "",
            "src": "/uploads/img-mq633sfj-d9bcaf.jpg"
          },
          {
            "tone": "water",
            "label": "",
            "src": "/uploads/img-mq633wwv-967bd3.jpg"
          },
          {
            "tone": "meadow",
            "label": "Honey Bear's Birthday Ride",
            "src": "/uploads/img-mq624ds7-91ede4.webp"
          }
        ],
        "features": [
          {
            "id": "feat-rated",
            "icon": "star",
            "title": "Highly Rated",
            "sub": "Loved by guests"
          },
          {
            "id": "feat-family",
            "icon": "leaf",
            "title": "Family-Owned",
            "sub": "Since 1997"
          },
          {
            "id": "feat-pets",
            "icon": "check",
            "title": "Pet Friendly",
            "sub": "Leashed pups welcome"
          },
          {
            "id": "feat-season",
            "icon": "cal",
            "title": "Open Seasonally",
            "sub": "Apr 3 → Oct 18, 2026"
          }
        ],
        "badge": {
          "value": "4.1 ★",
          "label": "Google Rated",
          "sub": "Loved by our guests"
        }
      },
      "bookingBar": {
        "title": "Check availability",
        "sub": "We match you to the right site by hand — so availability always ends with a quick call.",
        "button": "Call to check availability",
        "buttonLink": {
          "action": "phone",
          "value": ""
        }
      },
      "stays": {
        "eyebrow": "Stay with us",
        "title": "Pick your *spot*.",
        "sub": "Three ways to stay, all with a fire ring, picnic table, and shade."
      },
      "amenities": {
        "photo": "/uploads/img-mq6332ej-77a4fb.webp",
        "badge": {
          "title": "Free Wi-Fi & Cable TV",
          "sub": "On every site"
        }
      },
      "gallery": {
        "title": "Life at *Butterfly*"
      },
      "activities": {
        "heading": "Fun for the *whole family*.",
        "button": "See all activities",
        "buttonLink": {
          "action": "page",
          "value": "activities"
        }
      },
      "journal": {
        "eyebrow": "From the journal",
        "title": "Featured stories"
      },
      "reviews": {
        "eyebrow": "Guest words",
        "title": "Loved by *families*.",
        "ratingValue": "4.1",
        "ratingCount": "321",
        "items": [
          {
            "id": "rev-choi",
            "name": "Choi Durante",
            "meta": "June 2026",
            "quote": "If you're looking for a peaceful, quiet, and relaxing camping trip with fun activities and a nice pool for the kids, this is definitely the place to go. All of the employees were amazing — especially Melissa, Don, and Lexi.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2taWlJYa3RURmhuUTI1VFQxOTBUVUpRZEZoWFgyYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2taWlJYa3RURmhuUTI1VFQxOTBUVUpRZEZoWFgyYxAB?hl=en"
          },
          {
            "id": "rev-susan",
            "name": "Susan Hopper",
            "meta": "June 2026",
            "quote": "The owners, Don and Melissa were absolutely wonderful and their daughter Angie went above and beyond to help us with our reservation and check in. The entire area was immaculate inside and out.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21WVGJVaEdjR0l5UVVJMVR6Y3laR3hIV1VkRlRXYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21WVGJVaEdjR0l5UVVJMVR6Y3laR3hIV1VkRlRXYxAB?hl=en"
          },
          {
            "id": "rev-david",
            "name": "David Ahrens",
            "meta": "June 2026",
            "quote": "We have stayed here a few times in the past and always come back. The owners are obviously improving the property and the location is convenient to many area hot spots.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21oQmIzSlhTM28yZEhCR2EwdDNUMlo0VGxabVZHYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21oQmIzSlhTM28yZEhCR2EwdDNUMlo0VGxabVZHYxAB?hl=en"
          },
          {
            "id": "rev-michelle",
            "name": "Michelle Dutton",
            "meta": "May 2026",
            "quote": "Family owned and operated! Extremely friendly people, quiet, very clean, from the little shop down to the public bathrooms!",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT25SblVXUkNUbk5GVTJWSWJYQjNVVU52TFUxNlVFRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT25SblVXUkNUbk5GVTJWSWJYQjNVVU52TFUxNlVFRRAB?hl=en"
          },
          {
            "id": "rev-tara",
            "name": "Tara Demiceli",
            "meta": "May 2026",
            "quote": "Love Butterfly campground! We have been going for several years. Warm friendly atmosphere. The family that owns this establishment are wonderful!",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21GRFkwaGxWV2hPY1hKQmQzQjRObTVKV2w5aE9FRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21GRFkwaGxWV2hPY1hKQmQzQjRObTVKV2w5aE9FRRAB?hl=en"
          },
          {
            "id": "rev-dawn",
            "name": "Dawn White",
            "meta": "June 2026",
            "quote": "Close to many central Jersey attractions and close to the shore. The Letho family genuinely care about their seasonals and all who camp here.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2kxTU1sOUNXbTUzYTNKSU0zVjBlRVUzYWtNeWEwRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2kxTU1sOUNXbTUzYTNKSU0zVjBlRVUzYWtNeWEwRRAB?hl=en"
          },
          {
            "id": "rev-donna",
            "name": "Donna A",
            "meta": "June 2026",
            "quote": "This is a small, family owned, family friendly campground. It has nice, clean, roomy lots. Even the seasonal lots are kept neat. The staff was very friendly and helpful.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21Sd2IyRklUbTVVTWxwaFNEVlVkM2hWY2pKV1ZtYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21Sd2IyRklUbTVVTWxwaFNEVlVkM2hWY2pKV1ZtYxAB?hl=en"
          },
          {
            "id": "rev-alissa",
            "name": "Alissa Walters",
            "meta": "September 2025",
            "quote": "To say this place is amazing would be an understatement. From the grounds to the family activities we had fun from the time we came to the time we left.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT21kSk1GSXpZMVZEVDFCQ1JDMXRSSE5UU1U1alRYYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT21kSk1GSXpZMVZEVDFCQ1JDMXRSSE5UU1U1alRYYxAB?hl=en"
          },
          {
            "id": "rev-courtney",
            "name": "Courtney Tarpy",
            "meta": "2025",
            "quote": "If you are looking for a campground with genuine owners, this is for you! My husband and I were camping under a unique situation and everyone could not have been more accommodating!",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2xBd1NWOTJUVVJJWWxSeVNVZFdTV05ZT0hWeWIxRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2xBd1NWOTJUVVJJWWxSeVNVZFdTV05ZT0hWeWIxRRAB?hl=en"
          },
          {
            "id": "rev-genairo",
            "name": "Genairo Zapata",
            "meta": "September 2025",
            "quote": "The staff is fantastic helpful and friendly the sites are clean the bathrooms and showers are super clean the seasonals are nice and respectful we love this place 100 stars if I could",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2tWWWVYaHpUVWxPY25WVVMwbzFNRWw0VkRKaE9IYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2tWWWVYaHpUVWxPY25WVVMwbzFNRWw0VkRKaE9IYxAB?hl=en"
          },
          {
            "id": "rev-rebecca",
            "name": "Rebecca Pilotte",
            "meta": "2025",
            "quote": "The bathrooms! I have never seen cleaner bathrooms and showers at a park or campground. Even my son was impressed by how clean the showers were.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSUQ3MTdXeDd3RRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSUQ3MTdXeDd3RRAB?hl=en"
          },
          {
            "id": "rev-ian",
            "name": "Ian Ian",
            "meta": "2025",
            "quote": "The cleanest park I have ever seen! All the campers that stay there are super welcoming and their kids played with ours the entire weekend. Saturday was filled with activities for the kids. Definitely going back here!",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSUNiNVBiU3JnRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSUNiNVBiU3JnRRAB?hl=en"
          },
          {
            "id": "rev-lenny",
            "name": "Lenny Goldberg",
            "meta": "September 2025",
            "quote": "The cabin was clean and nicely set up. The grounds and amenities are well kept.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sCi9DQUlRQUNvZENodHljRjlvT2xnMllsRnhjMTlaTjFaQmQzaDFlVEl3ZFVvM2MzYxAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:Ci9DQUlRQUNvZENodHljRjlvT2xnMllsRnhjMTlaTjFaQmQzaDFlVEl3ZFVvM2MzYxAB?hl=en"
          },
          {
            "id": "rev-adam",
            "name": "Adam",
            "meta": "2025",
            "quote": "This campground has a very large, beautiful, clean swimming pool, dunk tank at special times, Karaoke, arcade, wagon rides, and many more activities. Good sized sites. You don't feel like you're on top of each other.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSUNyMllTaG5RRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSUNyMllTaG5RRRAB?hl=en"
          },
          {
            "id": "rev-shelby",
            "name": "Shelby Pape",
            "meta": "2024",
            "quote": "We had two adults and two kids in a rustic cabin. It met all expectations. There was AC, two windows, picnic bench outside, fire pit and space for a pack n play in the cabin.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChZDSUhNMG9nS0VJQ0FnSURwMnNQcVlnEAE!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChZDSUhNMG9nS0VJQ0FnSURwMnNQcVlnEAE?hl=en"
          },
          {
            "id": "rev-gail",
            "name": "Gail Feaver",
            "meta": "2024",
            "quote": "This was our first visit to Butterfly Camping Resort, and it will not be our last. The campsites are very shady and well maintained.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSURKaFlqMnV3RRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSURKaFlqMnV3RRAB?hl=en"
          },
          {
            "id": "rev-kimberly",
            "name": "Kimberly Nardone",
            "meta": "2022",
            "quote": "The family who owns it are SUPER nice, helpful people who go out of their way to make your camping experience a great one. Saturday activities for the kids and family were very fun and well run.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChZDSUhNMG9nS0VJQ0FnSUNhNF9pQ0t3EAE!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChZDSUhNMG9nS0VJQ0FnSUNhNF9pQ0t3EAE?hl=en"
          },
          {
            "id": "rev-ria",
            "name": "Ria Mungal",
            "meta": "2024",
            "quote": "Rented a cabin for 3 nights. It was really clean and comfortable. This is a great place for a family trip. Loved the saltwater pool, it was very clean.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSURKOXNXMTFnRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSURKOXNXMTFnRRAB?hl=en"
          },
          {
            "id": "rev-kathleen",
            "name": "Kathleen Popola",
            "meta": "2023",
            "quote": "This is a great campground with plenty of sites, all nestled inside the woods. We parked our 42' fifth wheel without any problems.",
            "url": "https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChdDSUhNMG9nS0VJQ0FnSURlbzV2YXFRRRAB!2m1!1s0x0:0x90057f5895916569!3m1!1s2@1:ChdDSUhNMG9nS0VJQ0FnSURlbzV2YXFRRRAB?hl=en"
          }
        ],
        "pullQuote": {
          "text": "The kind of place you measure summers by — our kids count down to it all year.",
          "attribution": "— A returning Butterfly family"
        }
      },
      "nearby": {
        "eyebrow": "Beyond the grounds",
        "title": "Explore *nearby*.",
        "sub": "Big days out or quiet ones — there's plenty within easy reach of the campground.",
        "items": [
          {
            "id": "near-sixflags",
            "distance": "~5 miles",
            "title": "Six Flags Great Adventure",
            "desc": "Coasters, a drive-through safari, and the water park — minutes from your site."
          },
          {
            "id": "near-shore",
            "distance": "~45 minutes",
            "title": "The Jersey Shore",
            "desc": "Point Pleasant, Seaside, and the boardwalks make an easy day trip east."
          },
          {
            "id": "near-bogs",
            "distance": "Adjacent",
            "title": "Butterfly Bogs & Trails",
            "desc": "Pine Barrens cranberry bogs and quiet hiking right next to the grounds."
          }
        ]
      },
      "location": {
        "eyebrow": "Find us",
        "title": "360 Butterfly Rd. Jackson, NJ 08527",
        "ctaEyebrow": "Ready to book?",
        "ctaTitle": "Give us a ring."
      },
      "finalCta": {
        "eyebrow": "Your weekend's waiting",
        "title": "Ready to *escape*?",
        "sub": "Pick a weekend, grab the family, and let us match you to the right site — it starts with a call.",
        "secondaryLabel": "View accommodations",
        "primaryLink": {
          "action": "phone",
          "value": ""
        },
        "secondaryLink": {
          "action": "page",
          "value": "rv"
        }
      }
    },
    "contactCtas": {
      "eventsCallLink": {
        "action": "phone",
        "value": ""
      },
      "blogCallLink": {
        "action": "phone",
        "value": ""
      }
    },
    "announcement": {
      "on": true,
      "notices": [
        {
          "id": "promo",
          "type": "Promotion / Limited Offer",
          "on": true,
          "icon": "cal",
          "theme": "green",
          "text": "Fall Gas Saving Specials! Starts Friday, September 11th",
          "link": ""
        },
        {
          "id": "maintenance",
          "type": "Maintenance",
          "on": false,
          "icon": "alert",
          "theme": "amber",
          "text": "UNDER CONSTRUCTION NOTICE! - Some information on our website may be inaccurate or not up-to date!",
          "link": ""
        },
        {
          "id": "coming",
          "type": "Coming Soon",
          "on": false,
          "icon": "star",
          "theme": "purple",
          "text": "New cabins launching next season!",
          "link": ""
        },
        {
          "id": "tips",
          "type": "Tips / Information",
          "on": false,
          "icon": "info",
          "theme": "blue",
          "text": "Reserve by phone — we still answer it ourselves.",
          "link": ""
        }
      ],
      "title": "Site under construction.",
      "text": "Some photos and event listings are placeholders for the 2026 season.",
      "link": "#rv"
    },
    "contact": {
      "phone": "(732) 928-2107",
      "email": "butterflycamp2107@gmail.com",
      "addr1": "360 Butterfly Road",
      "addr2": "Jackson, New Jersey 08527"
    },
    "officeHours": [
      [
        "Mon – Thu",
        "10am – 5pm"
      ],
      [
        "Friday",
        "10am – 10pm"
      ],
      [
        "Saturday",
        "9am – 9pm"
      ],
      [
        "Sunday",
        "9am – 6pm"
      ]
    ],
    "season": {
      "opens": "April 3, 2026",
      "closes": "October 18, 2026",
      "chip": "Apr 3 → Oct 18, 2026",
      "ribbon": true
    },
    "nav": {
      "rv": true,
      "tent": true,
      "cabins": true,
      "activities": true,
      "events": true,
      "gallery": true,
      "blog": true,
      "rules": true
    },
    "grounds": {
      "source": "selected"
    },
    "photos": [
      {
        "id": "p-mrrxf3re-d9ms",
        "label": "IMG_1193",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxf3sr-bb22c6.webp",
        "featured": true
      },
      {
        "id": "p-mrrxf5z3-5ij5",
        "label": "IMG_1182",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxf5zz-eee3e6.webp",
        "featured": true
      },
      {
        "id": "p-mrrxf7q8-disn",
        "label": "IMG_1190",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxf7rj-28a9b7.webp"
      },
      {
        "id": "p-mrrxfc23-yey",
        "label": "IMG_1233",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxfc1d-e65262.webp"
      },
      {
        "id": "p-mrrxfhie-itbc",
        "label": "IMG_1389",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxfhi1-9adc41.webp"
      },
      {
        "id": "p-mrrxfn78-2gjw",
        "label": "IMG_1400",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxfn5h-adaaee.webp"
      },
      {
        "id": "p-mrrxfs6d-aucy",
        "label": "IMG_1418",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxfs56-dbca7c.webp",
        "featured": true
      },
      {
        "id": "p-mrrxgcye-iho4",
        "label": "IMG_1424",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxgcxe-cdb6e6.webp"
      },
      {
        "id": "p-mrrxjoi3-cw4z",
        "label": "att.f2feWCqy-N6oOUWF41rpiJOJjPKpSiKSpldG4Jnh1sM",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxjoiy-9b685c.webp"
      },
      {
        "id": "p-mrrxjrnv-k8zw",
        "label": "att.GIUYAYNyn7EPdP1IPzl4prtYYkPsmFRjKl_-RseCKJ8",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxjro7-03d91c.webp"
      },
      {
        "id": "p-mrrxjwhc-6n0d",
        "label": "att.zQIoyXHmNZ1F5FGJGV3NLdUWf7ycp--Dh658iq5KoXU",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxjwgx-461e3a.webp"
      },
      {
        "id": "p-mrrxjyp6-fxtm",
        "label": "att.oM4-3u6ttjGnaWyuJ_byTtJQjfb_OGgsFRH7i25jP40",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxjyqn-1110ef.webp"
      },
      {
        "id": "p-mrrxk1dq-lkm",
        "label": "att.Hc1zITpTDLJ_sEY-ls_jtX5_LRTQ-ALXunnymtHfPJ8",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxk1f9-185f09.webp"
      },
      {
        "id": "p-mrrxk6io-i8f",
        "label": "IMG_1430 (1)",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrxk6hd-4504ce.webp"
      },
      {
        "id": "p-mrrve7b0-jlzx",
        "label": "IMG_1630",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrve7ae-3559bd.webp"
      },
      {
        "id": "p-mrrvegmb-dz0t",
        "label": "IMG_1723",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvegmh-5da05e.webp"
      },
      {
        "id": "p-mrrvf0zd-joey",
        "label": "IMG_1740",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvf0z7-992896.webp"
      },
      {
        "id": "p-mrrvfcjz-kj1n",
        "label": "IMG_1750",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvfck5-740e92.webp"
      },
      {
        "id": "p-mrrvff6a-5gnn",
        "label": "IMG_8629",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvff56-e3b3d8.webp"
      },
      {
        "id": "p-mrrvfjns-fj7o",
        "label": "IMG_1755",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvfjnf-e71e5c.webp"
      },
      {
        "id": "p-mrrvflsm-kt1s",
        "label": "IMG_8639",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvflsk-c2dab1.webp"
      },
      {
        "id": "p-mrrvfnti-ez83",
        "label": "att.-2rLkxsE8x0bd7eNbaRzbCpIpSXWd0aZ1kvGNkc9Ouc",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvfntp-5aff63.webp"
      },
      {
        "id": "p-mrrvfqoz-5b81",
        "label": "att.8jhGsz7g2vnlmAPTt1pzlHjPbswlGpblDH9dClQEfmo",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvfqn1-2ab4a9.webp"
      },
      {
        "id": "p-mrrvfsqz-i4j6",
        "label": "att.H0NtBbBrhqV2R-c8y2G0KZLHFS7WMv2y7d5p_s5CAwM",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvfsr7-07920d.webp"
      },
      {
        "id": "p-mrrvfutt-jj7n",
        "label": "att.VfdaP_-zwCrHZb9bfMoDqXlydfDRkOqR0C7enZDs9Qo",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvfuty-a10eae.webp"
      },
      {
        "id": "p-mrrvfxq5-c8xn",
        "label": "20260718_202611",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvfxnn-b5554e.webp"
      },
      {
        "id": "p-mrrvfzmd-djpz",
        "label": "att.aRZqwHWlGns9EdGnQmDMkOzTN9HybWWSemrYKBcw4KA",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvfzlp-a164bb.webp"
      },
      {
        "id": "p-mrrvg28c-34fs",
        "label": "att.90psslBQ3tKJ3FPA0gdawmDTG9SgfuoEAgho71fLDVI",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvg28j-685d87.webp"
      },
      {
        "id": "p-mrrvg3sc-95bi",
        "label": "att.MBDyq-CdnP8N_2rSnpT1jn0c8fCJJfGDTscg83_jYz8",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvg3sk-6f81f2.webp"
      },
      {
        "id": "p-mrrvg5gv-9p0q",
        "label": "att.kcW3zN83WHW9_41jSFGwTxysymuveRnD8RWYsuOjusw",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvg5g7-3040a3.webp"
      },
      {
        "id": "p-mrrvgdtc-e00f",
        "label": "att.F5PfYo-XHONSvtIeFqweBvzN8WUF1QLUQDv71fn2W-0",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvgdt7-85d2d7.webp"
      },
      {
        "id": "p-mrrvggaj-b2h4",
        "label": "att.LttrwgG0q5oCcExQ4q83WnVLvWjGo3YBI8DkdIBcyes",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvgg9z-5c88f7.webp"
      },
      {
        "id": "p-mrrvgiwe-e2od",
        "label": "att.2E1hz7oomxxj8fz_7rPnxJVM6fCsV-WZEBvcuD-_HnE",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvgivq-66a104.webp"
      },
      {
        "id": "p-mrrvgli3-5txr",
        "label": "att._oXm7cv3OBk_LTYhivEzf3aXpVcm7CZwURnWIh1EBcU",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrrvglhq-91df9b.webp"
      },
      {
        "id": "p-mrfgqptc-ba62",
        "label": "Butterfly Map",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mrfgqq62-8fe9f1.webp",
        "featured": true
      },
      {
        "id": "p-mq92b178-3qkr",
        "label": "120879300_2036251223175437_701698116991187617_n123",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92b16u-a50430.webp"
      },
      {
        "id": "p-mq92b3p2-f3q6",
        "label": "121044485_2036251289842097_8028254200751895934_n4",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92b3oc-dd5f5f.webp"
      },
      {
        "id": "p-mq92b8r2-ce1k",
        "label": "121053291_2036250556508837_3526612212299272327_n41",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92b8qk-78790c.webp"
      },
      {
        "id": "p-mq92bano-lbjo",
        "label": "121057422_2036250166508876_8116310323903383675_n3123",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92banb-0f2a15.webp",
        "featured": true
      },
      {
        "id": "p-mq92beuu-if4s",
        "label": "121069956_2036251483175411_7484394267268133692_n3",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92beud-7f9fdd.webp"
      },
      {
        "id": "p-mq92bgiy-dalj",
        "label": "121073362_2036251249842101_57687129309732433_n13",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92bgij-5677b6.webp"
      },
      {
        "id": "p-mq92bjlq-fbai",
        "label": "121075332_2036251463175413_6536899773935883799_n5",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92bjla-06cc87.webp",
        "featured": true
      },
      {
        "id": "p-mq92bnmd-9ir6",
        "label": "121078401_2036251406508752_1828074130471899477_n3",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92bnm0-b325ac.webp"
      },
      {
        "id": "p-mq92c6qs-4uwi",
        "label": "121111064_2036251549842071_564278678978504278q1_n",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92c6qa-3b2fb5.webp"
      },
      {
        "id": "p-mq92cd0v-9b8f",
        "label": "121111064_2036251549842071_5642786789785042781_n",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92cd0g-557b18.webp"
      },
      {
        "id": "p-mq92cfmd-429n",
        "label": "476098545_1092934575964926_4777064671746829161_n",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92cflz-e4c7ef.webp"
      },
      {
        "id": "p-mq92cmih-aig9",
        "label": "butterfly-pool-picnic",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92cmi1-ffcf0a.webp"
      },
      {
        "id": "p-mq92cytv-5a5y",
        "label": "img-mq4ldf2y-c750c9",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92cytd-364442.webp"
      },
      {
        "id": "p-mq92d0gy-1cnb",
        "label": "img-mq4ldf3j-60269b",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92d0gl-e9fb3c.webp"
      },
      {
        "id": "p-mq92d24h-jjzs",
        "label": "img-mq4ldf5r-966914",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92d243-2de550.webp"
      },
      {
        "id": "p-mq92d41x-dmzw",
        "label": "img-mq4ldf7b-1fe8ae",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92d41g-619e09.webp"
      },
      {
        "id": "p-mq92d6m5-iog3",
        "label": "img-mq4ldf7x-8b4a4b",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92d6lr-b04a0e.webp"
      },
      {
        "id": "p-mq92d7wz-9gcq",
        "label": "img-mq4ldf8j-52e1cf",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92d7wj-fddf90.webp"
      },
      {
        "id": "p-mq92d982-3qmt",
        "label": "img-mq4ldf9p-fe1b10",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92d97o-5e12a3.webp"
      },
      {
        "id": "p-mq92daoi-j31q",
        "label": "img-mq4ldf92-2e7d52",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dao2-9670fe.webp"
      },
      {
        "id": "p-mq92dbyd-ddbd",
        "label": "img-mq4ldfa6-92226a",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dbxw-10c2e8.webp"
      },
      {
        "id": "p-mq92dmc6-2mk2",
        "label": "img-mq4ldfaq-294312",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dmbp-07e45a.webp"
      },
      {
        "id": "p-mq92dnq3-b4f1",
        "label": "img-mq4ldfbx-9eef70",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dnpp-cc0444.webp"
      },
      {
        "id": "p-mq92dozi-h4qf",
        "label": "img-mq4ldfcg-8dba95",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92doyz-867923.webp"
      },
      {
        "id": "p-mq92dqgn-djq",
        "label": "img-mq4ldfd2-c353b4",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dqg8-3eeb7c.webp"
      },
      {
        "id": "p-mq92dsp2-6gg9",
        "label": "img-mq4ldfdm-53ec92",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dsoo-1099f2.webp"
      },
      {
        "id": "p-mq92dv85-3po3",
        "label": "img-mq4ldfe7-adf902",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dv7n-62cb81.webp"
      },
      {
        "id": "p-mq92dxiv-s2w",
        "label": "img-mq4ldfer-7acab3",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dxif-8adeba.webp"
      },
      {
        "id": "p-mq92dzht-88u1",
        "label": "img-mq4ldffa-333490",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92dzhg-183a3e.webp"
      },
      {
        "id": "p-mq92e0rr-5s7v",
        "label": "img-mq4ldffs-1be159",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92e0qp-7da090.webp"
      },
      {
        "id": "p-mq92e1yh-aumd",
        "label": "img-mq4ldfgg-6f2046",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92e1xz-c6b1df.webp"
      },
      {
        "id": "p-mq92e44m-1zqb",
        "label": "img-mq4ldfgx-bce326",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92e43z-b52b3a.webp"
      },
      {
        "id": "p-mq92e5ca-9dbg",
        "label": "img-mq4ldfhe-e1e1e2",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92e5bs-ff8095.webp"
      },
      {
        "id": "p-mq92e6jr-dzri",
        "label": "img-mq4ldfi0-ddc7e5",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92e6jd-52d3ed.webp"
      },
      {
        "id": "p-mq92e83u-abv0",
        "label": "img-mq4ldfiq-b3e52f",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92e83f-cca6c1.webp"
      },
      {
        "id": "p-mq92e9g8-ifr2",
        "label": "img-mq4ldfje-c2f8ff",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92e9fq-3b0a5e.webp"
      },
      {
        "id": "p-mq92eamc-c6l3",
        "label": "img-mq4ldfk0-e4990d",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ealv-486c33.webp",
        "featured": true
      },
      {
        "id": "p-mq92ebkh-2fbp",
        "label": "img-mq4ldfkk-245d11",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ebk4-6fbb43.webp"
      },
      {
        "id": "p-mq92eczt-64t4",
        "label": "img-mq4ldfl2-d3d176",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ecz9-012b7d.webp"
      },
      {
        "id": "p-mq92ee0u-cg3e",
        "label": "img-mq4ldflk-d483e1",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ee0i-e3198c.webp"
      },
      {
        "id": "p-mq92ef4y-96g4",
        "label": "img-mq4ldfm2-5aa300",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ef4l-02b9e0.webp"
      },
      {
        "id": "p-mq92eg9k-61tz",
        "label": "img-mq4ldfmp-b9efc5",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92eg95-802aae.webp"
      },
      {
        "id": "p-mq92eh94-hyex",
        "label": "img-mq4ldfnn-00db9b",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92eh8p-a54ca5.webp"
      },
      {
        "id": "p-mq92ei54-cm42",
        "label": "img-mq4ldfo5-0ce351",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ei4r-f328c7.webp"
      },
      {
        "id": "p-mq92ej1y-5shw",
        "label": "img-mq4ldfok-00b3d0",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ej1l-3093cd.webp",
        "featured": true
      },
      {
        "id": "p-mq92ejxm-bnkw",
        "label": "img-mq4ldfp0-79aee1",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ejx9-3afbed.webp",
        "featured": true
      },
      {
        "id": "p-mq92ektx-itei",
        "label": "img-mq4ldfpg-fe179f",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ektk-c6f321.webp"
      },
      {
        "id": "p-mq92em6k-5gua",
        "label": "img-mq4ldfr0-d11d77",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92em5l-e7e0e5.webp",
        "featured": true
      },
      {
        "id": "p-mq92en4v-csbb",
        "label": "img-mq4ldfrf-1ba437",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92en4h-e64a88.webp"
      },
      {
        "id": "p-mq92eofj-f28",
        "label": "img-mq4ldfrq-f0541f",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92eoen-ac52d9.webp",
        "featured": true
      },
      {
        "id": "p-mq92eq0m-imon",
        "label": "img-mq4ldfte-51bce9",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92eq04-6141e6.webp"
      },
      {
        "id": "p-mq92er82-76fr",
        "label": "img-mq4ldfua-bdf5a1",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92er7m-f8ca77.webp"
      },
      {
        "id": "p-mq92esv1-604s",
        "label": "img-mq4ldfuw-9ccea2",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92esug-c28d02.webp",
        "featured": true
      },
      {
        "id": "p-mq92eu42-k2fw",
        "label": "img-mq4ldfvg-75a141",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92eu3i-1a90e9.webp"
      },
      {
        "id": "p-mq92euxt-94ia",
        "label": "img-mq4ldfwk-17de63",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92euxg-165cd1.webp",
        "featured": true
      },
      {
        "id": "p-mq92ewi6-iuk4",
        "label": "img-mq4ldfy4-a74e5d",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ewhl-196a5e.webp",
        "featured": true
      },
      {
        "id": "p-mq92exws-d6rs",
        "label": "img-mq4ldfzo-537709",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92exwb-c577b4.webp"
      },
      {
        "id": "p-mq92ezm2-87m8",
        "label": "img-mq4ldg2n-ea2489",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ezlp-fe2729.webp"
      },
      {
        "id": "p-mq92fld2-1kda",
        "label": "img-mq4ldg3o-e1d1c9",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92flcg-36ad68.webp"
      },
      {
        "id": "p-mq92fo32-4yhb",
        "label": "img-mq4ldg6q-37a8ba",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92fo2o-d3a4d0.webp"
      },
      {
        "id": "p-mq92fqrr-h6vb",
        "label": "img-mq4ldg7k-0c69db",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92fqrc-fba409.webp",
        "featured": true
      },
      {
        "id": "p-mq92ft1i-inn9",
        "label": "img-mq4ldg8e-88c81f",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ft15-a6477a.webp",
        "featured": true
      },
      {
        "id": "p-mq92fv5l-bmkz",
        "label": "img-mq4ldg8v-713feb",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92fv54-cdc272.webp"
      },
      {
        "id": "p-mq92fyvp-6kin",
        "label": "img-mq4ldg9m-77ba78",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92fyva-6fc8f8.webp",
        "featured": true
      },
      {
        "id": "p-mq92g1o0-jczv",
        "label": "img-mq4ldg13-4f39ba",
        "cat": "Events",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92g1nl-0f82fb.webp"
      },
      {
        "id": "p-mq92g7dm-cox1",
        "label": "img-mq4ldg36-a28a5a",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92g7d8-441513.webp"
      },
      {
        "id": "p-mq92g8wj-icqw",
        "label": "img-mq4ldg50-2e05e5",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92g8vt-9d8c1d.webp"
      },
      {
        "id": "p-mq92gazn-gnmw",
        "label": "img-mq4ldg64-37bcd6",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gaz4-659b53.webp"
      },
      {
        "id": "p-mq92gcag-l4zt",
        "label": "img-mq4ldga7-4d419e",
        "cat": "People",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gca0-a3d75a.webp"
      },
      {
        "id": "p-mq92gdpj-hn6i",
        "label": "img-mq6rsgy2-f501db",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gdp6-e23e22.webp"
      },
      {
        "id": "p-mq92ghem-a44i",
        "label": "img-mq6w4xpm-36a696",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92ghe9-c1b3a0.webp"
      },
      {
        "id": "p-mq92gjdr-4djg",
        "label": "img-mq6wn64a-352464",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gjdc-34df36.webp"
      },
      {
        "id": "p-mq92gm0n-bq9f",
        "label": "img-mq6x1std-4d8e4c",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gm09-f1bd32.webp"
      },
      {
        "id": "p-mq92gnis-2epc",
        "label": "img-mq7zdpcf-37ac84",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gnia-3dfb47.webp"
      },
      {
        "id": "p-mq92gpag-elza",
        "label": "img-mq7ze617-e42cb7",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gp9y-dfa54a.webp"
      },
      {
        "id": "p-mq92gr1g-5txd",
        "label": "img-mq7zfbu7-f5ba94",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gr11-5134ae.webp"
      },
      {
        "id": "p-mq92gsvj-4jl9",
        "label": "img-mq7zgook-520f81",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gsv5-b43d78.webp"
      },
      {
        "id": "p-mq92gx21-90ew",
        "label": "img-mq75tw52-84954a",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gx0y-6e24ca.webp"
      },
      {
        "id": "p-mq92gyx2-iuvk",
        "label": "img-mq76dp0o-77435d",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92gywl-07cf22.webp"
      },
      {
        "id": "p-mq92h4dp-8nlt",
        "label": "img-mq76e1xa-8926eb",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92h4d4-d7c0c3.webp"
      },
      {
        "id": "p-mq92hap3-gxf",
        "label": "img-mq77wo6o-a1f74e",
        "cat": "Resort",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92haon-7d8bb4.webp"
      },
      {
        "id": "p-mq92hd62-czfy",
        "label": "img-mq622qzm-833a8d",
        "cat": "Pool",
        "h": 240,
        "tone": "neutral",
        "src": "/uploads/img-mq92hd5p-937213.webp"
      }
    ],
    "blog": [
      {
        "id": "b-mrruxdgi-6jrk",
        "title": "Christmas in July!",
        "excerpt": "Santa Clause is Coming to Town!!",
        "body": "This weekend was nothing short of magical! From the moment the Christmas decorations went up, the campground was filled with holiday cheer and smiling faces.\n\nSaturday kicked off with our morning walk, followed by campers getting creative and decorating their own personalized stockings. After working up an appetite, everyone enjoyed our Super Saturday ice cream sundaes while eagerly waiting for the big man himself…\n\n🎅 Santa Claus arrived! He brought plenty of gifts for all the wonderful boys and girls—and even surprised moms, dads, husbands, and wives with a little Christmas magic of their own!\n\nA little rain couldn't dampen our holiday spirit! We kept the fun going with reindeer games indoors until the skies cleared. Then it was time for the moment everyone had been waiting for...\n\n⛸️ We officially hit the ice on our brand-new outdoor ice-skating rink! Seeing so many families laughing, skating, playing in the SNOW and making unforgettable memories was the perfect ending to an incredible Christmas in July celebration.\n\n[photo:/uploads/img-mrrvflsk-c2dab1.webp]\n\nThank you to everyone who celebrated with us and helped make this weekend so special. We can't wait to see what memories we'll make together next!\n\n❤️🎄 Merry Christmas... in July! 🎄❤️",
        "cat": "Resort News",
        "date": "Jul 19, 2026",
        "readMins": 3,
        "status": "published",
        "tone": "meadow",
        "src": "/uploads/img-mrrv2gb1-6cc69b.webp",
        "slug": "christmas-in-july",
        "pinned": true,
        "updatedAt": "2026-07-25T17:23:00.485Z"
      },
      {
        "id": "b-mr9jf822-fene",
        "title": "A Star-Spangled Weekend at Butterfly Camping Resort!",
        "excerpt": "A Glimpse of America's 250th Birthday!",
        "body": "Our 4th of July Weekend was lit up not only by fireworks but by smiles, laughter, and unforgettable memories shared throughout the campground!\n\nMost of our campers arrived on Thursday and Friday, filling the park with excitement as another holiday weekend got underway. Once everyone was settled in, Friday night kicked off with a special appearance by Mr. Magico! His amazing tricks and interactive performance left both children and adults scratching their heads and wondering, \"How did he do that?\" The magic was the perfect way to start the weekend.\n\nSaturday seemed to fly by in the blink of an eye! We began our day with our 9:00 AM Morning Walk, where campers enjoyed a relaxing stroll around the campground while catching up with friends and meeting new faces. Afterward, many gathered to create patriotic crafts and celebrate America's birthday in red, white, and blue style.\n\nOf course, all that crafting worked up an appetite! Campers headed over for our Super Saturday Ice Cream Social, where everyone enjoyed their favorite ice cream creations loaded with endless toppings. Once the sweet treats were finished, it was time to cool off at the Dunk Tank! Friends, family members, and even a few brave volunteers took their turns getting soaked as laughter echoed across the baseball field.\n\nAfter drying off, everyone climbed aboard for our candy-filled Wagon Ride, one of our favorite campground traditions. Then it was time to put on the best red, white, and blue outfits and head over for a very competitive game of B.I.N.G.O!\n\nSaturday night's B.I.N.G.O was one for the books. The room became so quiet you could hear a pin drop as numbers were called. Then suddenly, the silence would be broken by an excited shout of \"BINGOOOO!\" followed by cheers from campers throughout the room. It was a close and exciting night for everyone involved.\n\nSunday was just as memorable. While some campers packed up and returned to their everyday routines, many stayed to enjoy a third night of camping with us. We started the morning with another campground walk, sharing stories, laughs, and favorite Butterfly memories while talking about all the fun still to come.\n\nThe afternoon brought more crafts, refreshing snow cones, and another candy-filled Wagon Ride around the campground. As evening arrived, campers gathered for Karaoke Night. We were treated to some incredible performances, lots of laughs, and plenty of encouragement from the crowd. Whether singing solo, with friends, or simply cheering others on, everyone had a great time. New friendships were formed, memories were made, and smiles could be seen everywhere.\n\nBefore we knew it, Monday morning had arrived. Campers packed up their sites, said their goodbyes, and headed home after an incredible holiday weekend filled with fun, friendship, and family memories.\n\nThank you to everyone who spent their 4th of July Weekend with us. Weekends like this are what camping is all about!\n\n🌈 Looking ahead, our next big event is Color FUN Weekend on July 11th! Join us for a colorful weekend packed with exciting activities, laughter, and plenty of opportunities to make unforgettable memories.\n\nCan't make it? Be sure to check back for our next blog to see all the colorful fun you missed!\n\nUntil next time, happy camping from all of us at Butterfly Camping Resort! 🦋",
        "cat": "Resort News",
        "date": "Jul 6, 2026",
        "readMins": 3,
        "status": "published",
        "tone": "meadow",
        "src": "/uploads/img-mrryouu3-4da147.webp",
        "pinned": true,
        "slug": "a-star-spangled-weekend-at-butterfly-camping-resort",
        "updatedAt": "2026-07-19T15:39:49.591Z"
      },
      {
        "id": "b-mqha14lx-854t",
        "title": "Memorial Day Weekend at Butterfly Camping Resort",
        "excerpt": "Rain or shine, this weekend was ours!",
        "body": "There’s something special about Memorial Day Weekend at Butterfly, and this year was no exception! Rain or shine, our campers came ready for fun, crazy rain dances, singing our hearts out like we were on a world tour, and together we made memories that will last all season long.\n\nThe weekend began with morning crafts, where our youngest campers showed off their creativity and had a blast making projects with friends. Of course, no Saturday is complete without a sweet treat, and our Super Saturday Ice Cream Social had everyone smiling as they enjoyed their favorite flavors.\n\nAs temperatures warmed up, campers eagerly headed to the pool, racing to be among the first to take a dip. While a few rain showers rolled through, they only added to the excitement, giving us a fun sneak peek of the splashes and laughs that await during our upcoming Wet & Wild Weekend (August 1st).\n\nFor those looking for a more relaxing way to spend the day, families gathered around our back pond with fishing rods in hand. The fishing was fantastic all weekend, with several campers reeling in impressive catches—including bass weighing up to 5 pounds! It was great seeing families spending quality time together by the water, sharing stories and celebrating each big catch.\n\nSaturday evening brought everyone together in the pavilion for a lively game of B.I.N.G.O. The competition was fierce, the excitement was contagious, and plenty of lucky winners went home with prizes and bragging rights.\n\nSunday kept the fun going with one of our favorite campground traditions—karaoke! Campers of all ages took the microphone, sang their hearts out, and cheered one another on. The laughter and applause could be heard throughout the campground.\n\nNo Memorial Day Weekend would be complete without our famous Root Beer Floats. Friends and families gathered together to enjoy this classic campground treat while catching up and sharing stories. The day continued with dinner among friends, where campers came together to enjoy great food, great company, and the welcoming community that makes Butterfly Camping Resort feel like home.\n\nAs we reflect on the weekend, we are grateful for every camper who joined us. Memorial Day Weekend is more than just the unofficial start of summer—it's a time to connect, relax, and create memories with the people who matter most. From crafts and ice cream to fishing, karaoke, and Root Beer Floats, the weekend was filled with smiles, laughter, and unforgettable moments.\n\nThank you for making Memorial Day Weekend such an awesome weekend. We look forward to seeing everyone back for another weekend full of fun, friendship, and adventure at Butterfly Camping Resort!\n\n🦋See you around the campground! 🦋",
        "cat": "Resort News",
        "date": "Jun 16, 2026",
        "readMins": 3,
        "status": "published",
        "tone": "meadow",
        "src": "/uploads/img-mrrysn6u-8b7427.webp",
        "pinned": true,
        "slug": "memorial-day-weekend-at-butterfly-camping-resort",
        "updatedAt": "2026-07-19T15:42:45.306Z"
      },
      {
        "id": "b-mrbg000v-seo1",
        "title": "Camping Near Six Flags Great Adventure: A Local's Guide",
        "slug": "camping-near-six-flags-great-adventure",
        "cat": "Area Guide",
        "excerpt": "Make the park a day trip — and the campfire your evening.",
        "body": "If Six Flags Great Adventure is on your family's summer list, here's a local secret: the best part of a Six Flags trip isn't the hotel afterward. It's coming back to a quiet, wooded campsite, lighting a fire, and letting the kids wind down under the pines instead of in a parking-lot lobby.\n\nButterfly Camping Resort sits in Jackson, New Jersey — the same town as the park. The drive from our gate to the Six Flags entrance is an easy one, usually 25 to 35 minutes depending on traffic, so you can be there when the gates open and back at your site before the marshmallows come out.\n\nHere's how our campers usually do it. Arrive the day before and get set up without rushing — check-in is relaxed, and the kids can burn off the car ride at the playground or the pool. Head to the park early the next morning (weekday mornings are noticeably lighter all summer), spend the day on the coasters and the Safari, and come home to a campfire dinner. If you have a second park day in you, Hurricane Harbor is right there too.\n\nA few honest tips from families who make this trip every year: bring a soft cooler and eat breakfast at your site before you go — park food adds up fast. Buy park tickets online ahead of time. And plan for the kids to be wiped out on the ride back, because they will be.\n\nWhy camp instead of a hotel? Space, mostly. A full-hookup RV site here runs a fraction of a Jackson-area hotel room in peak season, and instead of one room you get a campfire, a picnic table, the pool, and a weekend full of campground activities — most summer Saturdays we have crafts, our Super Saturday Ice Cream Social, wagon rides, B.I.N.G.O in the pavilion, and more going on right here.\n\nWe have 128 RV sites (water-and-electric from $74 a night, full hookups with 30- or 50-amp service too), 6 wooded tent sites, and 8 cabins if you're not towing anything. Our season runs April 3 through October 18.\n\nIf you're planning a Six Flags trip this summer, give the office a call at 732-928-2107 and we'll help you pick a site. See you at the campfire after the coasters!\n\n🦋 Happy camping from all of us at Butterfly Camping Resort! 🦋",
        "date": "Jul 26, 2026",
        "readMins": 2,
        "status": "published",
        "tone": "meadow",
        "src": "",
        "pinned": false,
        "updatedAt": "2026-07-26"
      },
      {
        "id": "b-mrbg000v-seo2",
        "title": "Six Flags Plus a Campfire: A Two-Day Family Weekend Itinerary",
        "slug": "six-flags-camping-weekend-itinerary",
        "cat": "Area Guide",
        "excerpt": "A ready-made plan: one day of coasters, one day of camp.",
        "body": "Some families come to Jackson for Six Flags and discover camping. Others come for the camping and discover Six Flags is up the road. Either way, two days is the sweet spot — one for the park, one for the campground. Here's the itinerary our campers swear by.\n\nFriday evening: arrive and settle in. Get the camper leveled or the cabin unpacked while there's still daylight, walk the loop to get your bearings, and keep dinner simple — hot dogs over the fire never let anyone down. Most summer Fridays there's something happening at the pavilion, so check the events board when you check in.\n\nSaturday: park day. Eat a real breakfast at your site (trust us — this is the money-saving move of the whole weekend), pack water bottles and sunscreen, and get to Six Flags for opening. Do the big coasters early before the lines build, take the Safari drive-through in the afternoon when everyone needs to sit down in the air conditioning, and head home when the kids hit the wall. Back at camp, it's showers, a campfire dinner, and s'mores. If anyone still has energy, Saturday night at Butterfly usually means B.I.N.G.O in the pavilion — and it gets competitive.\n\nSunday: camp day. This is the day the weekend actually becomes a vacation. Morning walk around the grounds, a swim in the pool, fishing rods down at the back pond — campers pulled in bass up to five pounds this season — and one more unhurried meal outside before you pack up.\n\nWhat to book: a full-hookup RV site if you're bringing the camper (30-amp from $85 a night, 50-amp from $93), a wooded tent site if you're keeping it classic, or one of our 8 cabins if you want beds, A/C, and a mini-fridge with zero setup. Cabins go first on summer weekends, so call ahead — 732-928-2107.\n\nOne weekend, two very different kinds of fun, and everybody rides home tired and happy. That's the whole recipe.\n\n🦋 See you around the campground! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "published",
        "tone": "meadow",
        "src": "/uploads/img-mrryzkvv-2242c1.webp",
        "pinned": false,
        "updatedAt": "2026-07-19T15:48:09.855Z"
      },
      {
        "id": "b-mrbg000v-seo3",
        "title": "Things to Do in Jackson, NJ Beyond Six Flags",
        "slug": "things-to-do-in-jackson-nj-beyond-six-flags",
        "cat": "Area Guide",
        "excerpt": "Outlets, pine trails, bugs the kids will love, and more — all close to camp.",
        "body": "Everyone knows Jackson for Six Flags — but ask the families who camp with us all season, and they'll tell you the area has a lot more going for it. Here's what's worth exploring within a short drive of your campsite.\n\nJackson Premium Outlets. About 20 minutes away, with the usual lineup of name-brand stores. This is the reliable rainy-day plan, and more than a few campers have picked up a forgotten hoodie or swimsuit here mid-weekend.\n\nThe Pine Barrens. We're right at the edge of New Jersey's million-acre pine forest, and it's the reason the campground smells the way it does. Sandy trails, cedar creeks, and some of the darkest night skies in the state. You don't have to drive anywhere to enjoy it — it starts at your site — but day hikes and kayak liveries are close by if you want to go deeper.\n\nAllaire State Park. A restored 1800s iron-making village with living-history days, easy walking trails, and a narrow-gauge steam train that younger kids adore. An easy half-day trip.\n\nInsectropolis. A quirky little bug museum in nearby Toms River that kids talk about for the rest of the weekend. Tarantulas, hissing cockroaches, a honeybee wall — it's weirder and better than it sounds.\n\nThe Jersey Shore. Point Pleasant and the ocean beaches are well under an hour away — close enough for a beach morning and still back at camp for dinner. We wrote a whole separate guide to shore day trips, so check the Journal for that one.\n\nAnd honestly? Some of the best things to do around here are the ones happening at the campground. Between the pool, the back pond, wagon rides, ice cream socials, and whatever's on the events calendar that weekend, plenty of families arrive with big sightseeing plans and end up never leaving their campsite. We take that as a compliment.\n\nPlanning a trip? Our season runs April 3 through October 18 — call 732-928-2107 and we'll help you pick your weekend.\n\n🦋 Happy exploring from all of us at Butterfly Camping Resort! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "draft",
        "tone": "meadow",
        "src": "",
        "pinned": false,
        "updatedAt": "2026-07-08T02:12:15.007Z"
      },
      {
        "id": "b-mrbg000v-seo4",
        "title": "Jersey Shore Day Trips From Your Campsite",
        "slug": "jersey-shore-day-trips-from-your-campsite",
        "cat": "Area Guide",
        "excerpt": "Sand in the morning, pines and a campfire by night.",
        "body": "Here's a combination you can't get from a shore-town motel: a morning on the beach and an evening around a campfire in the pines. Camping in Jackson puts you close enough to the Jersey Shore for an easy day trip — without shore-town prices or shore-town traffic following you home to your site.\n\nThe beaches our campers hit most are the classic Ocean County stretch — Point Pleasant Beach with its boardwalk and aquarium, Seaside Heights when the kids are craving rides and boardwalk games, and the quieter state-park sands of Island Beach if you'd rather have dunes than crowds. Depending on the beach and the summer traffic, you're generally looking at 30 to 45 minutes each way — closer to an hour for Island Beach.\n\nThe move that makes it work: go early. Leave camp after breakfast, get a parking spot before the day-trippers, do your beach hours before the afternoon peak, and be heading back inland while everyone else is still circling for parking. You'll be in the pool rinsing off the salt by mid-afternoon and lighting the fire by dinner.\n\nWhat to bring back to camp: nothing beats a boardwalk lunch, but skip the boardwalk dinner — a cooler of burgers tastes better at your own picnic table anyway. Do budget for beach badges in summer; most Jersey beach towns require them for anyone 12 and up.\n\nA rhythm a lot of our seasonal families settle into: beach Saturday, campground Sunday. One day of sand and waves, one day of the pool, the pond, and whatever's happening on the events calendar. The kids get two completely different adventures out of a single weekend, and nobody has to unpack twice.\n\nThat's the real advantage of camping here instead of shore-side — you get the ocean when you want it and the peace of the pines when you don't.\n\nOur season runs April 3 through October 18. Call the office at 732-928-2107 to book your basecamp.\n\n🦋 Sandy toes and campfire smoke — happy camping! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "draft",
        "tone": "meadow",
        "src": "",
        "pinned": false,
        "updatedAt": "2026-07-08T02:12:15.007Z"
      },
      {
        "id": "b-mrbg000v-seo5",
        "title": "First Time RV Camping? What to Know Before You Book",
        "slug": "first-time-rv-camping-what-to-know",
        "cat": "Camping Tips",
        "excerpt": "Everything a new RV family needs to know, minus the jargon.",
        "body": "Every summer we welcome families making their very first RV trip — some in a brand-new camper, some in a borrowed one, all of them a little nervous about hookups, dump stations, and doing it wrong in front of strangers. Here's the honest truth: everyone here was a beginner once, and campground people are the friendliest teachers you'll ever meet. This is what we'd tell you over the office counter.\n\nFirst, understand the site types. A water-and-electric site gives you exactly that — fresh water and power at your site. A full hookup site adds a sewer connection, which means your sinks and shower drain all weekend without a trip to the dump station. If it's your first time, full hookup is worth it purely for the peace of mind. (We wrote a separate plain-English guide to 30-amp vs 50-amp service — short version: match it to your RV's plug.)\n\nSecond, know your rig's numbers before you book. Length, and whether you need 30- or 50-amp power. That's genuinely all a campground needs to put you on the right site. If backing a trailer into a spot makes you sweat, ask about a pull-thru site — you drive in one end and out the other, no reversing required.\n\nThird, the arrival routine is simpler than the internet makes it look: park, level, chock the wheels, connect electric first, then water, then sewer. Ten minutes once you've done it twice. And if anything has you stumped, ask your neighbor — RV people love being asked.\n\nWhat it costs here: our water-and-electric sites start at $74 a night, 30-amp full hookups at $85, and 50-amp at $93, with weekly and monthly rates that bring those numbers down if you're staying longer. Fire ring and picnic table come standard, and there's wifi on the full-hookup sites.\n\nLast tip: don't overplan the first trip. Book a weekend, keep meals simple, and let the campground do the entertaining — between the pool, the pond, and Saturday activities, your only real job is lighting the campfire.\n\nCome practice on us. Call 732-928-2107 and tell us it's your first trip — we'll set you up right.\n\n🦋 You've got this — happy camping! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "draft",
        "tone": "meadow",
        "src": "",
        "pinned": false,
        "updatedAt": "2026-07-08T02:12:15.007Z"
      },
      {
        "id": "b-mrbg000v-seo6",
        "title": "What Is a Full Hookup Site? 30 Amp vs 50 Amp, Explained Simply",
        "slug": "what-is-a-full-hookup-site-30-amp-vs-50-amp",
        "cat": "Camping Tips",
        "excerpt": "The plain-English guide to campground hookups.",
        "body": "If you're new to RV camping, the booking questions can feel like a pop quiz. Full hookup or water-and-electric? 30 amp or 50? Here's the whole thing explained the way we explain it at the office window — no jargon, no judgment.\n\nA full hookup site has three connections waiting at your site: water, electric, and sewer. Water fills your fresh tank and runs your faucets. Electric powers everything from the A/C to the coffee maker. Sewer is the one people forget to appreciate — it means your gray water (sinks, shower) and black water (toilet) drain right at your site, so you never have to pack up and drive to a dump station mid-weekend.\n\nA water-and-electric site skips the sewer connection. Your tanks hold everything until you empty them on the way out. Perfectly fine for a short stay or a light crew — and it's the budget-friendly option, starting at $74 a night here.\n\nNow the amp question. 30-amp and 50-amp describe how much electricity your RV can draw, and your RV already made this decision for you — look at its power cord. A 30-amp plug has three prongs; a 50-amp plug has four. Thirty amps comfortably runs one air conditioner and your basics. Fifty amps is for bigger rigs that run two A/C units, a residential fridge, and a microwave all at once without tripping anything.\n\nThe rules of thumb: match the site to your cord and you'll never think about it again. A 50-amp rig can plug into a 30-amp site with a cheap adapter — you'll just have to take turns with the big appliances. And if it's a hot July weekend, that second air conditioner is exactly why 50-amp owners book 50-amp sites.\n\nWhat it costs at Butterfly: 30-amp full hookup sites start at $85 a night ($545 weekly), and 50-amp full hookups at $93 ($601 weekly). Both come with a fire ring, picnic table, and wifi, with pull-thru sites available if backing in isn't your favorite hobby.\n\nStill not sure which site fits your rig? Call us at 732-928-2107 with your RV's length and plug type and we'll sort it out in two minutes flat.\n\n🦋 Happy camping from all of us at Butterfly Camping Resort! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "published",
        "tone": "meadow",
        "src": "/uploads/img-mrrz9obm-377d73.webp",
        "pinned": false,
        "updatedAt": "2026-07-19T15:56:04.995Z"
      },
      {
        "id": "b-mrbg000v-seo7",
        "title": "Cabin Camping for Families Who Don't Own a Tent",
        "slug": "cabin-camping-for-families-without-a-tent",
        "cat": "Camping Tips",
        "excerpt": "Campfires and s'mores, real beds and A/C — no gear required.",
        "body": "Every family has one member who loves the idea of camping and one who has questions about the sleeping-on-the-ground part. Good news: you don't have to choose between them. Our cabins give you the whole camping experience — the campfire, the s'mores, the kids running free — with a real bed, air conditioning, and a door that locks.\n\nWe have 8 cabins here at Butterfly in three flavors. The Bear Bone Cabin is exactly what it sounds like — the simple, classic camping cabin. The Deluxe Cabin adds more room and comfort for bigger crews. And the Cottage is the full roof-over-your-head experience for families who want maximum easy. Whichever you pick, you're steps from everything the campground offers.\n\nHere's what's waiting inside every cabin: beds, air conditioning and heat, a mini-fridge and microwave, lighting and outlets, and free wifi. Trash pickup is on us too.\n\nWhat you bring is the personal stuff: sheets and pillows, towels, toiletries, a cooler with ice, and cookware if you're planning to cook. That's the entire packing list — no tent poles, no air mattresses, no praying the rain fly holds.\n\nAnd this is the part first-time cabin campers don't expect: you're not camping \"lite.\" You still get the fire ring and the picnic table. Your kids still do the ice cream socials, the wagon rides, the pool, the crafts, and Saturday night B.I.N.G.O with everybody else. You still fall asleep to the pines. You just do it on a mattress, and nobody has to learn how to level a trailer.\n\nCabins are our smallest category — 8 of them against 128 RV sites — so summer weekends and holiday weekends go fast, and event weekends go fastest. If a specific weekend matters to you, call early: 732-928-2107.\n\nCome try camping the easy way. We think the tent skeptic in your family is going to become a regular.\n\n🦋 See you at the campfire! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "published",
        "tone": "meadow",
        "src": "",
        "pinned": false,
        "updatedAt": "2026-07-08T02:12:15.007Z"
      },
      {
        "id": "b-mrbg000v-seo8",
        "title": "Seasonal Campsite 101: What It Is and Why NJ Families Love It",
        "slug": "seasonal-campsite-101-nj",
        "cat": "Camping Tips",
        "excerpt": "One site, all season — the summer-house alternative more families are choosing.",
        "body": "There's a group of campers here who never pack up. Their camper stays on its site from opening day to closing day, the flamingo lights stay strung, and every Friday they just... drive up and start the weekend. They're our seasonals, and if you've ever wondered whether that life is for you, here's the plain-English version.\n\nA seasonal campsite means you rent one site for the entire camping season — here at Butterfly, that's April 3 through October 18. Your RV stays parked and set up the whole time. No towing, no reservations, no setup and breakdown every single weekend. Your site becomes your family's summer place.\n\nWhy do families do it? Do the math on how they actually camp. If you're the family that comes up most weekends anyway, paying by the night or even by the month starts to add up — and the towing, packing, and setup eats half of every Saturday. Seasonal flips that: one payment, zero setup, and your weekend starts the moment you park the car. For a lot of Jersey families it's the affordable answer to a shore house, at a fraction of the cost and without the Parkway traffic.\n\nThe part people underestimate is the community. Seasonals get a whole season of Saturday ice cream socials, B.I.N.G.O nights, karaoke, holiday weekends, and morning walks with the same familiar faces — there's a Seasonal Breakfast in the spring and a Seasonal Farewell Dinner in September, and by then the kids on your loop are a pack. Ask anyone who's done a season: the friendships are the thing that keeps them coming back, year after year.\n\nIs it right for you? Our honest test: if you camped four or more weekends last summer and canceled at least one trip because hooking up the trailer felt like too much, you're a seasonal waiting to happen.\n\nSeasonal sites are limited and they're personal — the right site matters when it's yours all summer. Call the office at 732-928-2107 for this year's seasonal availability and rates, and we'll walk the grounds with you to find your spot.\n\n🦋 Maybe next season the flamingo lights are yours! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "draft",
        "tone": "meadow",
        "src": "",
        "pinned": false,
        "updatedAt": "2026-07-08T02:12:15.007Z"
      },
      {
        "id": "b-mrbg000v-seo9",
        "title": "Labor Day Weekend Camping in NJ: What's Happening at Butterfly (2026)",
        "slug": "labor-day-weekend-camping-nj-2026",
        "cat": "Events",
        "excerpt": "Close out summer 2026 the right way — September 4–6 at Butterfly.",
        "body": "Summer doesn't end on a school calendar — it ends around a campfire on Labor Day weekend. September 4th through 6th is the last big holiday weekend of our 2026 season, and if our Memorial Day and 4th of July weekends were any preview, you do not want to watch this one from home.\n\nHere's the shape of a Butterfly holiday weekend, for anyone who hasn't done one yet. Friday the campground fills up and comes alive as everyone gets settled. Saturday is the big day — morning walks and crafts, our Super Saturday Ice Cream Social, wagon rides around the grounds, swimming while the pool still has that September warmth, and a pavilion packed for Saturday night B.I.N.G.O, where it gets quiet enough to hear a pin drop right up until somebody yells BINGO. Sunday brings more of everything, usually capped with karaoke — and because Monday's a holiday, Sunday night feels like a second Saturday.\n\nLabor Day has its own flavor, too. The crowds are a touch softer than July, the evenings are cooler and made for campfires, and the back pond fishing picks up as the water cools — campers pulled bass up to five pounds out of there this season. It's summer with the edges sanded off.\n\nAnd it's not quite goodbye. Our season runs through October 18th, with the Seasonal Farewell Dinner on September 19th and our Halloween weekends still ahead in October — Trick or Treat weekend on the 3rd and the Grave Yard Get Down on the 10th. Labor Day is your reminder to grab those dates before they're gone.\n\nNow the practical part, and we mean this kindly: holiday weekends book up. All of them — the 128 RV sites, the 6 tent sites, and especially the 8 cabins, which are always the first to go. If Labor Day at Butterfly sounds like your kind of send-off for summer 2026, call the office now at 732-928-2107.\n\nBring the family, bring the fishing rods, and let's give summer the goodbye it deserves.\n\n🦋 See you Labor Day weekend! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "draft",
        "tone": "meadow",
        "src": "",
        "pinned": false,
        "updatedAt": "2026-07-08T02:12:15.007Z"
      },
      {
        "id": "b-mrbg000v-seo10",
        "title": "Halloween Camping in NJ: Trick-or-Treat Weekends at Butterfly (2026)",
        "slug": "halloween-camping-nj-trick-or-treat-weekends-2026",
        "cat": "Events",
        "excerpt": "Costumes, campsite trick-or-treating, and the Grave Yard Get Down — October at Butterfly.",
        "body": "Here's a secret the summer-only crowd never learns: October might be the best month at the campground. The pines smell stronger, the campfires feel earned, the mosquitoes have packed it in — and at Butterfly, Halloween comes early, twice.\n\nOur 2026 Halloween lineup has two big weekends. October 3rd is Trick or Treat weekend, when the campground turns into the safest, most cheerful trick-or-treat route in New Jersey — kids in costume going site to site while campers hand out candy from decorated campsites. If you've only ever done doorbells and sidewalks, watching your kids trick-or-treat between campfires is something else entirely.\n\nThen October 10th brings the Grave Yard Get Down — our Halloween party weekend, and the name should tell you everything about how seriously our campers take it. Expect decorations that get more elaborate every year, costumes on kids and grown-ups alike, and the kind of Saturday night the pavilion was built for.\n\nA word about the decorating, because it deserves one: our campers do not mess around. Skeletons climbing camper ladders, cobwebbed awnings, full graveyard scenes — walking the loops after dark in early October is half the entertainment. Bring your own bin of decorations and join in; the kids notice every site that participates.\n\nOctober camping itself is the underrated part. Sweatshirt mornings, warm afternoons, campfire-perfect evenings, and fall color coming into the pines. The crowds are gentler than July, but don't let that fool you on the Halloween weekends — those book like holiday weekends, and our 8 cabins are especially popular once tent season cools off.\n\nThe season wraps October 18th with Your Weekend, Your Way — one last laid-back weekend to close out 2026. So the fall math is simple: two Halloween weekends, one farewell weekend, and then we all start counting down to April.\n\nCostumes, candy, campfires. Call 732-928-2107 and grab your October weekend before the skeletons take all the good sites.\n\n🦋 Happy haunting from all of us at Butterfly Camping Resort! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "draft",
        "tone": "meadow",
        "src": "",
        "pinned": false,
        "updatedAt": "2026-07-08T02:12:15.007Z"
      },
      {
        "id": "b-mrbg000v-seo11",
        "title": "Fishing at Butterfly: The Back Pond Guide",
        "slug": "fishing-at-butterfly-the-back-pond-guide",
        "cat": "Camping Tips",
        "excerpt": "Bass up to five pounds came out of our pond this season. Here's how to get yours.",
        "body": "Some campgrounds have a pond the way a hotel has a lobby fountain — nice to look at, nothing more. Ours is not that pond. This season alone, campers have pulled bass up to five pounds out of the back pond, and any given summer evening you'll find families lined along the bank with rods in hand and a cooler of drinks between them.\n\nThe back pond sits tucked behind the campground, away from the pool and pavilion bustle, which is exactly what makes it work — for the fish and for the parents. It's a short walk from any site, the banks are kid-friendly, and the pace is the opposite of everything else on a summer Saturday.\n\nWhen to go: early morning and the last hour before dusk are the honest answer, same as any pond in New Jersey. That said, our campers catch fish at every hour — Memorial Day weekend the fishing was fantastic literally all weekend. Summer evenings after dinner have become the unofficial community fishing hour.\n\nWhat to bring: nothing fancy. A basic spinning rod, a small box of soft plastics or spinnerbaits for the bass, and worms if the kids just want action. If your crew is new to fishing, this is a wonderful first pond — the stakes are low, the walk is short, and when a bass hits, the whole bank hears about it.\n\nThe house rules keep it good for everyone: it's catch and release, so today's five-pounder is still in there waiting for your kids next weekend. Handle the fish gently, wet your hands first, and get your photo quick — because nobody back home will believe you otherwise.\n\nAnd that's the real magic of the pond. Fish stories. The five-pounder gets retold at the ice cream social, grows an inch by B.I.N.G.O night, and by the end of the season it's legend. Every family that camps here long enough has one.\n\nPack the rods next trip — they take up no room and they buy you the quietest, best hour of the weekend. The bass are waiting.\n\n🦋 Tight lines from all of us at Butterfly Camping Resort! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "published",
        "tone": "meadow",
        "src": "/uploads/img-mrrz8fhb-403590.webp",
        "pinned": false,
        "updatedAt": "2026-07-19T15:55:04.269Z"
      },
      {
        "id": "b-mrbg000w-seo12",
        "title": "Color Run & Foam Party Weekend Is Coming July 11th!",
        "slug": "color-run-and-foam-party-weekend-july-11-2026",
        "cat": "Events",
        "excerpt": "Our most colorful weekend of 2026 — here's what to expect and what to pack.",
        "body": "Consider this your official warning: on July 11th, Butterfly Camping Resort will not be staying clean. Color Run & Foam Party weekend is almost here, and it's one of the most-anticipated weekends on our whole 2026 calendar.\n\nIf you've never done a color run, here's the idea — a fun run (emphasis on fun, zero emphasis on run) where you start in a white shirt and finish looking like a rainbow exploded, which it did, because that's the whole point. Color gets thrown, everyone laughs, and the photos are the best of the summer. It's for every age and every speed; plenty of \"runners\" walk it holding a toddler's hand.\n\nAnd then there's the foam party. We'll just say it: there is no version of a foam party that kids do not lose their minds over. Mountains of foam, music going, everybody soaked and giggling — it's the closest thing to a snow day in July.\n\nAll the usual Butterfly Saturday goodness is still on deck around it — the morning walk, crafts, the Super Saturday Ice Cream Social, wagon rides, and B.I.N.G.O to cap the night. Add the color and the foam on top, and Saturday the 11th is going to be a full day.\n\nWhat to pack for this one, learn from the veterans: a white shirt for every family member (that's the canvas!), clothes you genuinely don't care about, towels you feel the same way about, goggles or sunglasses for the foam, and a dry change of clothes for the walk back. A cheap poncho for the ride home in the car is a pro move. The color washes off people easily — shirts, though, wear it forever, which is kind of the trophy.\n\nSites for event weekends go quickly, and this is one of the big ones. If your summer needs a weekend where everybody comes home tie-dyed and worn out from laughing, call the office at 732-928-2107 and lock it in.\n\nWhite shirts ready? See you on the 11th!\n\n🦋 Let's get colorful, Butterfly! 🦋",
        "date": "Jul 7, 2026",
        "readMins": 2,
        "status": "published",
        "tone": "meadow",
        "src": "/uploads/img-mrrxf3sr-bb22c6.webp",
        "pinned": false,
        "updatedAt": "2026-07-19T15:55:23.736Z"
      }
    ],
    "events": [
      {
        "m": "April",
        "sum": "Opening Weekend · Easter Egg Hunt · Seasonal Breakfast · Spring Special Starts",
        "badge": "",
        "weeks": [
          {
            "id": "apr1",
            "title": "April 3 | Opening + Egg hunt!",
            "badge": "",
            "on": true,
            "bullets": [
              "The Countdown is Over!! Welcome Back Campers!",
              "Easter Egg Hunt Along with Egg Dying Activities!",
              "Spring into The Spring Specials!"
            ]
          },
          {
            "id": "apr2",
            "title": "April 25th | Seasonal Breakfast",
            "badge": "",
            "on": true,
            "bullets": [
              "Start the season right by a complimentary Butterfly Breakfast!"
            ]
          }
        ]
      },
      {
        "m": "May",
        "sum": "Pool opening · Mother's Day brunch · Memorial Day",
        "badge": "Pool opens",
        "weeks": [
          {
            "id": "may1",
            "title": "May 9th. Mother's Day Weekend",
            "badge": "Pool opens",
            "on": true,
            "bullets": [
              "Join us for a Mother's Day Breakfast. Mom's Eat FREE! Followed With a Handmade Craft For Mom With a Mom Only Craft Night!"
            ]
          },
          {
            "id": "may2",
            "title": "May 16th Appreciation of Our Nation!",
            "badge": "",
            "on": true,
            "bullets": [
              "This Weekend All Active Military, Veterains & First Responders will receive 50% Their ENTIRE weekend with valid ID! Must mention at time of booking for Discount! Thank You for Your Service & Dedication to Our Country & Communities!"
            ]
          },
          {
            "id": "may3",
            "title": "May 22nd- 25th. Memorial Day Weekend!",
            "badge": "",
            "on": true,
            "bullets": [
              "*3 Night Minimum*",
              "Kick off summer with crafts, bingo, wagon rides, ice cream sundaes, karaoke, a petting zoo, pony rides, and more! Make family memories that last all season long!"
            ]
          },
          {
            "id": "may4",
            "title": "May 30th Camp Candy Land! ",
            "badge": "Holiday",
            "on": true,
            "bullets": [
              "Step into a World of Candy Canes & Gumdrops! Candy Games With crafts and a HUGE Lollipop Hunt! "
            ]
          }
        ]
      },
      {
        "m": "June",
        "sum": "Birthday Bash · Father's Day · Pasta & Pirates",
        "badge": "",
        "weeks": [
          {
            "id": "jun1",
            "title": "June 6th Honey Bear's Birthday Bash!",
            "badge": "",
            "on": true,
            "bullets": [
              "Decorate cupcakes for Honey Bear's 29th Birthday! Followed with Super Saturday Social's, Wagon Ride with Honey Bear, & B.I.N.G.O!"
            ]
          },
          {
            "id": "jun2",
            "title": "June 13th Tye-Dye Weekend!",
            "badge": "",
            "on": true,
            "bullets": [
              "Let's Get Groovy & Tye Dye your own creation! Followed with Super Saturday Sundaes, Wagon Ride, & Crafts!"
            ]
          },
          {
            "id": "jun3",
            "title": "June 20th Father's Day Weekend!",
            "badge": "",
            "on": true,
            "bullets": [
              "Dad’s Eat FREE at our Father’s Day Breakfast! Followed by a handmade craft for Dad and our Dad’s Chili Cook-Off. Bring your appetite and your best chili recipe for a fun-filled day celebrating all the amazing dads!"
            ]
          },
          {
            "id": "jun4",
            "title": "June 27th Pasta & Pirates!",
            "badge": "",
            "on": true,
            "bullets": [
              "Find Captain Jack's Lost Treasure! After all that hunting sure does make a stomach hungry! Let's Make Cancer Walk The Plant With an All You Can Eat Pasta Dinner! Pool Party Movie, Yard Games, Face Paint, HUGE Basket Raffles & More! Best Dressed Pirate Wins a Prize!"
            ]
          }
        ]
      },
      {
        "m": "July",
        "sum": "Fourth of July · Christmas in July · Pork n Chicken",
        "badge": "Fourth of July",
        "weeks": [
          {
            "id": "jul1",
            "title": "July 2nd-5th 4th of July!",
            "badge": "",
            "on": true,
            "bullets": [
              "*3 Night Minimum*",
              "Celebrate Independence Day With Crafts, Dunk Tank, Ice cream Sundaes, Root Beer Floats, B.I.N.G.O, Karaoke, Decorate your site for a chance to win a prize!"
            ]
          },
          {
            "id": "jul2",
            "title": "July 11th Color Run & Foam Party!",
            "badge": "",
            "on": true,
            "bullets": [
              "Leave a Little Color Wherever you go! Race your friends, Family, or a Stranger! After let's get ready for an all-out foam party with games and music!"
            ]
          },
          {
            "id": "jul3",
            "title": "July 18th Christmas in July!",
            "badge": "",
            "on": true,
            "bullets": [
              "Step into a winter summer wonderland, Get a Picture and Make Crafts & Santa & His reindeer. Santa is handing out presents to all the boys & girls, Even Parents!! After let's enjoy the night with our BRAND-NEW Ice-Skating Rink! "
            ]
          },
          {
            "id": "jul4",
            "title": "July 25th Pork ·N· Chicken BBQ",
            "badge": "",
            "on": true,
            "bullets": [
              "29th Annual Pork  ·N· Chicken BBQ! Your Choice of Pork, or Chicken With Endless Slides! Join Us For a Day of Fun Followed With a Lollipop Hunt!"
            ]
          }
        ]
      },
      {
        "m": "August",
        "sum": "Wet ·N· Wild · Campfires & Cowboys · Steak Dinner · Lucky Roll Weekend · Camping In Calabria ",
        "badge": "",
        "weeks": [
          {
            "id": "aug1",
            "title": "August 1st Wet ·N· Wild Weekend",
            "badge": "",
            "on": true,
            "bullets": [
              "Get ready for an action-packed weekend of water-filled fun! The campground will turn into one giant splash zone as campers team up for an all-out water war! Bring your water guns, buckets, squirt toys, and plenty of towels because nobody stays dry during Wet N' Wild Weekend!"
            ]
          },
          {
            "id": "aug2",
            "title": "August 8th Campfires & Cowboys!",
            "badge": "",
            "on": true,
            "bullets": [
              "Saddle up for a rootin' tootin' good time! Enjoy FREE Pony Rides around our baseball field and grab your partner for some Wild West Line Dancing that'll have everyone stompin' their boots! Whether you're a seasoned cowboy or just lookin' for some family fun, there's something for everyone on the frontier!"
            ]
          },
          {
            "id": "aug3",
            "title": "August 15th Steak Dinner!",
            "badge": "",
            "on": true,
            "bullets": [
              "Love is in The Air! Well the smell of Our Steak Dinner is! Come Join Our 29th Annual Steak dinner where we supply our 12ft long BBQ, Steaks, Seasonings, Endless Sides, and Lollipop Hunt, All you have to bring is an appetite, a Drink, & Good Vibes!"
            ]
          },
          {
            "id": "aug4",
            "title": "August 22nd Lucky Roll!",
            "badge": "",
            "on": true,
            "bullets": [
              "Feeling lucky? Join us for Lucky Roll Weekend, where the fun never stops and the dice decide your fate! Gather your family and friends for exciting games of Left, Right, Center, friendly competition, laughter, and plenty of chances to win prizes."
            ]
          },
          {
            "id": "w-mqh8oyzq-6z1z",
            "title": "August 29th Camping in Calabria!",
            "badge": "",
            "on": true,
            "bullets": [
              "Mama Mia! You asked, We Delivered! Make the Pizza YOU Want & We Will Cook it to Perfection, Like a Chef's Kiss! Yard Games, Gift Raffle, Pool Party With a HUGE Movie Screen, & More! All Money Raised From This Event Will Go to The Greater Atlantic Cancer Fund!"
            ]
          }
        ]
      },
      {
        "m": "September",
        "sum": "Labor Day · Butterfly's Bufet Potluck · Seasonal Dinner · Fall Special Starts",
        "badge": "",
        "weeks": [
          {
            "id": "sep1",
            "title": "September 4th-6th Labor Day Weekend",
            "badge": "Labor Day Weekend",
            "on": true,
            "bullets": [
              "*3 Night Minimum*",
              "This weekend is not to be missed!! Get ready for our 29th Annual Horseshoe Tournament, 5th Annual Cornhole Tournament, Crafts, FREE Pony Rides & Petting Zoo B.I.N.G.O. Karaoke, Dunk Tank, Wagon Rides Root Beer Floats, Ice cream sundae's & WAY More!"
            ]
          },
          {
            "id": "sep2",
            "title": "September 11th",
            "badge": "",
            "on": true,
            "bullets": [
              "Fall Back On Our Fall Saving! (Fall Special's Start)",
              "Join Us For Our 1st Annual Butterfly's Potluck! "
            ]
          },
          {
            "id": "sep3",
            "title": "September 19th Seasonal Farewell Dinner",
            "badge": "",
            "on": true,
            "bullets": [
              "We're Wrapping Up An AMAZING Season The Right Way! Not only are we hosting our annual Farewell Dinner, but This Year... We're adding a twist! All those games you've been playing... or should we say practicing are about to be put to the test! Think of it as a Dinner with a show!",
              "Dinner, Games, Musix, Prizes, Bragging Rights!"
            ]
          }
        ]
      },
      {
        "m": "October",
        "sum": "Trick Or Treat · Graveyard Get Down · Your Weekend Your Way!",
        "badge": "Halloween",
        "weeks": [
          {
            "id": "oct1",
            "title": "October 3rd Trick or Treat weekend",
            "badge": "Halloween Weekend",
            "on": true,
            "bullets": [
              "Get ready for a Spooktacular Halloween! ",
              "Trick or Treat around the park! BEWARE!! There are judges all around the campground picking out the: Best overall costume, Scariest, Most Creative, Best Duo, Best Trio, AND the BEST Decorated Site!",
              "We have ONE more TRICK up our sleeve! Let just say this activity involves GLOW IN THE DARK paintballs Moving/ Stationary targets, and some that may get VERY CLOSE to you!!! FREE, fun, safe for ALL ages!"
            ]
          },
          {
            "id": "w-mqhbnwvk-e5vu",
            "title": "October 10th Grave Yard Get Down!",
            "badge": "",
            "on": true,
            "bullets": [
              "The spirits are rising and the party is just getting started! Join us for our Graveyard Get Down Halloween Costume Party, where the campground transforms into a glowing Halloween celebration! Whether you're a ghost, pirate, witch, zombie, or something completely unique, this is one Halloween weekend you won't want to miss!"
            ]
          },
          {
            "id": "w-mqhbnxdv-g9z8",
            "title": "October 18th Your Weekend, Your Way",
            "badge": "",
            "on": true,
            "bullets": [
              "No schedules. No rushing. Just pure camground fun at your own pace! Whether you're looking to challenge friends to a game of cornhole, show off your skills at horseshoes, spend time in the game room, relax by the campfire or simply enjoy the great outdoors, this weekend is all about doing what makes YOU happy! Reconnect with family, make memories with friends, and ebjoy the freedom to create the perfect camping getaway- your way."
            ]
          }
        ]
      }
    ],
    "pages": {
      "rv": {
        "card": {
          "t": "RV Sites",
          "sub": "128 full-hookup sites",
          "src": "/uploads/img-mq6wn64a-352464.webp"
        },
        "hero": {
          "eyebrow": "Accommodations · RV",
          "title": "128 full-hookup sites.",
          "sub": "30-amp and 50-amp options, with a fire ring, picnic table, and pine shade on every single site.",
          "stats": [
            [
              "128",
              "Sites"
            ],
            [
              "2",
              "Hookup tiers"
            ],
            [
              "30/50",
              "Amp options"
            ]
          ],
          "photo": "/uploads/img-mq6w4xpm-36a696.webp"
        },
        "pricing": {
          "eyebrow": "Pick your hookup",
          "title": "Pricing, per night.",
          "tiers": [
            {
              "id": "we",
              "pull": {
                "price": 76,
                "weekly": true,
                "weeklyPrice": 482,
                "monthly": false,
                "monthlyPrice": 0,
                "seasonal": false,
                "seasonalPrice": 0
              },
              "t": "Water & Electric",
              "price": 74,
              "popular": false,
              "feats": [
                "Water hookup",
                "Electric service",
                "Fire ring",
                "Picnic table",
                "Pine shade"
              ],
              "monthly": true,
              "weekly": true,
              "weeklyPrice": 468,
              "monthlyPrice": 1064,
              "seasonalPrice": 0
            },
            {
              "id": "30a",
              "pull": {
                "price": 88,
                "weekly": true,
                "weeklyPrice": 573,
                "monthly": false,
                "monthlyPrice": 0,
                "seasonal": false,
                "seasonalPrice": 0
              },
              "t": "30 Amp Full Hookup",
              "price": 85,
              "popular": true,
              "feats": [
                "Water, sewer, electric",
                "30-amp service",
                "Fire ring & table",
                "Pull-thru available",
                "Wifi included"
              ],
              "weekly": true,
              "weeklyPrice": 545,
              "monthlyPrice": 1284,
              "seasonalPrice": 0,
              "monthly": true
            },
            {
              "id": "50a",
              "pull": {
                "price": 97,
                "weekly": true,
                "weeklyPrice": 629,
                "monthly": false,
                "monthlyPrice": 0,
                "seasonal": false,
                "seasonalPrice": 0
              },
              "t": "50 Amp Full Hookup",
              "price": 93,
              "popular": false,
              "feats": [
                "Water, sewer, electric",
                "50-amp service"
              ],
              "weekly": true,
              "weeklyPrice": 601,
              "monthlyPrice": 1484,
              "seasonalPrice": 0,
              "monthly": true
            }
          ]
        },
        "amenities": {
          "eyebrow": "What's on every site",
          "title": "Everything you need.",
          "items": [
            "Fire ring",
            "Picnic table",
            "Pine shade",
            "Free wifi",
            "Bathhouse access",
            "Laundry room",
            "Camp store"
          ]
        },
        "reserveLink": {
          "action": "phone",
          "value": ""
        },
        "cta": {
          "title": "Ready to roll in?",
          "sub": "Call the office — we'll match your rig to the right site.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          },
          "secondaryLink": {
            "action": "email",
            "value": ""
          }
        }
      },
      "tent": {
        "card": {
          "t": "Tent Sites",
          "sub": "6 quiet wooded sites",
          "src": "/uploads/img-mq6x1std-4d8e4c.webp"
        },
        "hero": {
          "eyebrow": "Accommodations · Tent",
          "title": "6 quiet wooded sites.",
          "sub": "Tucked back from the RV loops, under a full pine canopy. Fire ring and picnic table on each.",
          "photo": "/uploads/img-mq75tw52-84954a.webp"
        },
        "intro": {
          "eyebrow": "The basics",
          "title": "Sleep on the ground, the old way.",
          "body": "Six wooded sites set well apart from the main loops. More quiet, more shade, and a proper sense of wilderness — even this close to the Jersey shore.",
          "price": 74,
          "tone": "fog",
          "photoLabel": "Tent site under pines",
          "src": "/uploads/img-mq6rsgy2-f501db.webp"
        },
        "features": [
          {
            "id": "f1",
            "title": "Fire ring & picnic table",
            "body": "Bring marshmallows and a folding chair — the rest is already waiting at your site.",
            "tone": "pine",
            "src": "/uploads/img-mq77x8rh-1bb02e.webp"
          },
          {
            "id": "f2",
            "title": "Bathhouse close by",
            "body": "Hot showers, flush toilets, and we keep it genuinely tidy. No long walks in the dark.",
            "tone": "water",
            "src": "/uploads/img-mq76dp0o-77435d.webp"
          },
          {
            "id": "f3",
            "title": "A real shaded canopy",
            "body": "Our trees shade keeps your site cool through the day, even in the thick of July.",
            "tone": "meadow",
            "src": "/uploads/img-mq76e1xa-8926eb.webp"
          }
        ],
        "reserveLink": {
          "action": "phone",
          "value": "732-928-2107"
        },
        "cta": {
          "title": "Want one of the six?",
          "sub": "They go fast — call early in the season to lock in your weekend.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          },
          "secondaryLink": {
            "action": "email",
            "value": ""
          }
        }
      },
      "cabins": {
        "card": {
          "t": "Cabins",
          "sub": "8 cozy cabins · sleeps 4–6",
          "src": ""
        },
        "hero": {
          "eyebrow": "Accommodations · Cabins",
          "title": "8 cozy cabins.",
          "sub": "Sleeps four to six. A/C, mini-fridge, and microwave inside — just bring your own linens.",
          "stats": [
            [
              "8",
              "Cabins"
            ],
            [
              "4–6",
              "Sleeps"
            ],
            [
              "$128.58 - $207.82",
              "From / night"
            ]
          ],
          "photo": "/uploads/img-mq6pro42-18b2d9.webp"
        },
        "units": [
          {
            "id": "cab-bear",
            "mode": "cards",
            "eyebrow": "The Cabins",
            "name": "Bear Bone Cabin",
            "price": 128.58,
            "blurb": "Our classic camp cabin — bunks for the kids, a queen for you, and a porch built for slow mornings.",
            "photos": [
              {
                "tone": "dusk",
                "label": "Bear Bone exterior at dusk",
                "src": "/uploads/img-mq6pro42-18b2d9.webp"
              },
              {
                "tone": "neutral",
                "label": "Bear Bone interior",
                "src": "/uploads/img-mq6pryut-143a71.webp"
              },
              {
                "tone": "pine",
                "label": "Shaded outdoor area",
                "src": "/uploads/img-mq783bpd-c43350.webp"
              }
            ],
            "specs": [
              [
                "Double Bed",
                "Two Bunk Beds"
              ],
              [
                "Sleeps 4 (2 Adults/2 Children)",
                "Tv"
              ],
              [
                "Mini Fridge",
                "A/C Unit "
              ],
              [
                "Front Porch",
                "Picnic Table, Fire Ring, Standing BBQ Charcoal Grill"
              ]
            ],
            "tags": []
          },
          {
            "id": "cab-deluxe",
            "mode": "cards",
            "eyebrow": "The Cabins",
            "name": "Deluxe Cabin",
            "price": 154.5,
            "blurb": "More room, more comfort. private bath, and a deck under the pines.",
            "photos": [
              {
                "tone": "water",
                "label": "Deluxe exterior",
                "src": "/uploads/img-mq6ptdvt-b339d6.webp"
              },
              {
                "tone": "meadow",
                "label": "Kitchenette & living space",
                "src": "/uploads/img-mq6ptrbw-cb9ff3.webp"
              },
              {
                "tone": "fog",
                "label": "Outdoor area",
                "src": "/uploads/img-mq78462i-849715.webp"
              }
            ],
            "specs": [
              [
                "Double Bed",
                "Twin Bunk Bed"
              ],
              [
                "Sleeps 4 (2 Adults/2 Children)",
                "Toilet, Shower, Sink"
              ],
              [
                "Microwave, Mini Fridge ",
                "Tv"
              ],
              [
                "Ac Unit",
                "Picnic Table, Fire Ring, Standing BBQ Charcoal Gril"
              ],
              [
                "Front Porch",
                ""
              ]
            ],
            "tags": []
          },
          {
            "id": "cab-cottage",
            "mode": "cards",
            "eyebrow": "The Cabins",
            "name": "Cottage",
            "price": 207.82,
            "blurb": "Our largest stay — a proper little house for families, with a full kitchen, two bedrooms, and a yard of its own.",
            "photos": [
              {
                "tone": "dusk",
                "label": "Cottage exterior",
                "src": "/uploads/img-mq6pu1jw-395b3b.webp"
              },
              {
                "tone": "neutral",
                "label": "Full kitchen, dining & Living room",
                "src": "/uploads/img-mq6puaaj-cc218c.webp"
              },
              {
                "tone": "pine",
                "label": "Private Bedroom",
                "src": "/uploads/img-mq6pv3h4-504d00.webp"
              }
            ],
            "specs": [
              [
                "Double Bed (Own Room) 2 Adults",
                "Twin Bunk Beds Two Kids"
              ],
              [
                "Full Bathroom",
                "Toilet, Shower, Sink"
              ],
              [
                "Half Kitchen",
                "Stove, Microwave, Sink, Full Size Fridge, Kitchen table Seats 4"
              ],
              [
                "Half Livingroom",
                "Futon, Tv, Chair"
              ],
              [
                "Front Porch",
                "Picnic Table, Fire Ring, Standing BBQ Charcoal Grill"
              ],
              [
                "Max",
                ""
              ]
            ],
            "tags": [
              "Double Bed (Own Room)",
              "Twin Bunk Beds",
              "Full Bathroom (Toilet, Shower, Sink)",
              "Half Kitchen (Stove, Microwave, Sink, Full Size Fridge, Kitchen table Seats 4)",
              "Half Livingroom (Futon, Tv, Chair)",
              "Front Porch",
              "Picnic Table, Fire Ring, Standing BBQ Charcoal Grill"
            ]
          }
        ],
        "bring": {
          "eyebrow": "You bring",
          "title": "The personal stuff.",
          "items": [
            "Sheets & pillows",
            "Towels",
            "Toiletries",
            "Cookware (if cooking)",
            "Cooler with ice"
          ]
        },
        "provide": {
          "eyebrow": "We provide",
          "title": "The rest.",
          "items": [
            "Bed, A/C & heat",
            "Mini-fridge & microwave",
            "Lighting & outlets",
            "Free wifi",
            "Trash pickup"
          ]
        },
        "reserveLink": {
          "action": "phone",
          "value": ""
        },
        "cta": {
          "title": "Pick a weekend.",
          "sub": "Eight cabins, one season — the good dates fill up by spring.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          },
          "secondaryLink": {
            "action": "email",
            "value": ""
          }
        }
      },
      "activities": {
        "hero": {
          "eyebrow": "Things to do!",
          "title": "22+ activities, all weekend long.!",
          "sub": "Sports, lawn games, indoor recreation, nature, family programs, and evenings by the fire.!",
          "stats": [
            [
              "22+",
              "Activities"
            ],
            [
              "4",
              "Outdoor courts"
            ],
            [
              "6",
              "Lawn games"
            ],
            [
              "Free",
              "For guests"
            ]
          ],
          "photo": "/uploads/img-mrryaeqf-8f68be.webp"
        },
        "items": [
          {
            "id": "act1",
            "title": "Outdoor Sports",
            "desc": "Basketball, kickball, and sand volleyball — courts kept in great shape all season.",
            "tags": [
              "Basketball",
              "Sand volleyball",
              "Ping Pong"
            ],
            "tone": "meadow",
            "src": "/uploads/img-mq7zdpcf-37ac84.webp"
          },
          {
            "id": "act2",
            "title": "Lawn Games",
            "desc": "Horseshoes, cornhole, ladder toss, and a giant Jenga set that somehow never stops getting used.",
            "tags": [
              "Horseshoes",
              "Cornhole",
              "Giant Jenga",
              "Gaga Pit"
            ],
            "tone": "dusk",
            "src": "/uploads/img-mq7ze617-e42cb7.webp"
          },
          {
            "id": "act3",
            "title": "Indoor Recreation",
            "desc": "An arcade and game room for the in-between hours, plus movie nights whenever the rain rolls in.",
            "tags": [
              "Arcade",
              "Game room"
            ],
            "tone": "night",
            "src": "/uploads/img-mrrycn2w-49cef6.webp"
          },
          {
            "id": "act4",
            "title": "Nature & Outdoors",
            "desc": "Hiking trails, guided nature walks, and a stocked fishing pond right on the property.",
            "tags": [
              "Hiking trails",
              "Bird watching",
              "Fishing pond",
              "Bog walk"
            ],
            "tone": "pine",
            "src": "/uploads/img-mq7zgook-520f81.webp"
          },
          {
            "id": "act5",
            "title": "Kids & Family",
            "desc": "A big playground, a splash pad for hot afternoons, and weekend programs the kids actually ask for.",
            "tags": [
              "Playground",
              "Craft table",
              "Family bingo"
            ],
            "tone": "water",
            "src": "/uploads/img-mq7zfbu7-f5ba94.webp"
          },
          {
            "id": "act6",
            "title": "Evening & Social",
            "desc": "Campfires, S'mores, New friendships, activities that are made from dawn to dusk!",
            "tags": [
              "Campfires",
              "Karaoke",
              "activities",
              "B.I.N.G.O",
              "Crafts",
              "Pool",
              "Game room"
            ],
            "tone": "dusk",
            "src": "/uploads/img-mrryiy0u-ba1eab.webp"
          }
        ],
        "cta": {
          "title": "Curious what's running this weekend?",
          "sub": "The schedule shifts week to week — give the office a call.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          }
        }
      },
      "events": {
        "hero": {
          "eyebrow": "2026 Season",
          "title": "What's happening, weekend by weekend.",
          "sub": "From the pool opening in May to October's Halloween extravaganza — 28+ themed weekends.",
          "photo": ""
        }
      },
      "gallery": {
        "hero": {
          "eyebrow": "Photographs",
          "title": "Moments from the campground.",
          "sub": "Tap any image to open it full-screen, then use the arrows or your keyboard to move through.",
          "photo": ""
        }
      },
      "blog": {
        "hero": {
          "eyebrow": "Field notes",
          "title": "Stories from the campground.",
          "sub": "Tips, season recaps, and the occasional bear sighting — straight from the family.",
          "photo": ""
        }
      },
      "rules": {
        "hero": {
          "eyebrow": "Good to know",
          "title": "Rules & regulations.",
          "sub": "A few simple rules keep the campground safe, quiet, and fun for every family — thanks for helping us keep it that way.",
          "photo": ""
        },
        "sections": [
          {
            "id": "rul-checkin",
            "title": "Check-in & check-out",
            "items": [
              "Check-in starts at 2pm and check-out is by 12pm noon — call the office if you need flexibility.",
              "All campers and visitors must register at the office on arrival.",
              "Arriving late? Call ahead and we'll leave instructions for you."
            ]
          },
          {
            "id": "rul-quiet",
            "title": "Quiet hours",
            "items": [
              "Quiet hours are 10pm to 8am, every night.",
              "Music and voices should stay at your own site — sound carries in the pines."
            ]
          },
          {
            "id": "rul-fires",
            "title": "Campfires",
            "items": [
              "Fires are out cold by midnight.",
              "Put your fire completely out before turning in or leaving your site.",
              "Please don't bring firewood from out of the state — it can carry invasive pests. Firewood is available at the camp store.",
              "All fires MUST be kept in the fire fing/BBQ"
            ]
          },
          {
            "id": "rul-pets",
            "title": "Pets",
            "items": [
              "Pet's Welcome! Pets Must be leashed, Cleaned up after, and never left unattended",
              "Not permitted in bathrooms, office, pool area, plaground, cabins, or cabin sites",
              "Pet's must not disturb or endanger other campers.",
              "Restricted Breeds: Rottweilers, Dobermans, Staffordshire Terriers, or any breeds commonly known as Pit Bull.",
              "Children under 13 must be accompanied by an adult while walking pets"
            ]
          },
          {
            "id": "rul-pool",
            "title": "Pool Rules",
            "items": [
              "NO Diving, Flipping, or Running in the pool area.",
              "No Food/snacks, Glass, or Pets Permitted",
              "No swimming after dark.",
              "No Lifeguard on Duty - Parents/ Guardians must supervise children at all times.",
              "No One under 16 permitted in the pool area without a parents or guardian (Not just any adult)",
              "Proper swimwear required; Swim diapers must be used for small children",
              "Pool WILL BE closed when owner is off premises.",
              "Do not swim alone",
              "Pool will be closed when thunder and or lightning is seen or heard"
            ]
          },
          {
            "id": "rul-visitors",
            "title": "Registered Guest & Visitors",
            "items": [
              "Visitors for the day (10 am- 9 pm). Overnight Visitors (10 am-12 pm Following day)",
              "Only guests listed on your reservation are considered registered.",
              "All others are considered visitors Must: Stop at the office to register before entering, Pay day-use parking fees. Visits are either 9 am-9 pm or 9 am- 12 pm (the following day)",
              "You are responsible for all gues/visitors during your entire stay ",
              "All people on your site have to be registered upon booking"
            ]
          },
          {
            "id": "rul-site",
            "title": "Trash & your site",
            "items": [
              "Bag your trash and drop it at the blue dumpsters — please don't leave it out overnight.",
              "Leave your site the way you found it — no digging, nailing into trees, or moving fire rings/ picnic tables"
            ]
          },
          {
            "id": "rul-mrf8t4gz-fvzy",
            "title": "Refunds",
            "items": [
              "No Refunds will be given for Illness, Weather, Rain, Fire, Date Changes, Early Departues, or Any cause beyond Butterfly Camping Resort's control."
            ]
          },
          {
            "id": "rul-mrf9qgbj-q3a",
            "title": "Cancelation Policy",
            "items": [
              "Cancellations made 15 or more days prior to the scheduled check-in date will receive a refund of all monies paid, minus the $35 processing fee.",
              "Holiday reservations (Memorial Day, 4th of July, and Labor Day) canceled 15 or more days prior to arrival will be charged the $35 processing fee plus one night's stay.",
              "No refunds or credits will be issued for cancellations made 14 days or less prior to the scheduled arrival date.",
              "No-shows will receive no refund or credit."
            ]
          },
          {
            "id": "rul-vehicles",
            "title": "Vehicles",
            "items": [
              "5mph throughout the entire park.",
              "One-way roads",
              "One vehicle per site, please — extra vehicles park in the overflow lot.",
              "Speeders Lose site!",
              "No dirt bikes, sxs, quads allowed"
            ]
          },
          {
            "id": "rul-mrfc3p3n-7bv8",
            "title": "Golf Carts",
            "items": [
              "Golf carts must be electric and carry proof of insurance. (Must give a copy of insurance to the office host)",
              "Drivers MUST follow all vehicle rules (5 mph speed limit)",
              "Unlicensed individuals are prohibited from operating golf carts"
            ]
          },
          {
            "id": "rul-mrfc8o29-6qyh",
            "title": "Alcohol, Substances, Firearms, & Fireworks",
            "items": [
              "Alcoholic beverages, illegal substances, firearms, ammunition, and all types of fireworks, including sparklers, are STRICTLY PROHIBITED anywhere on Butterfly Camping Resort property."
            ]
          },
          {
            "id": "rul-mrfc8q52-kxse",
            "title": "Gate Tags",
            "items": [
              "Each tag requires a $20 CASH deposit (Refundable upon return)",
              "Tags are issued only to registered vehicles at check-in",
              "Tags may NOT be used to open the gate for any other guest or visitor. ",
              "Anyone caught doing so will be asked to leave the park immediately, with no refund."
            ]
          },
          {
            "id": "rul-mrfc8qha-kzoc",
            "title": "Campsite Limits",
            "items": [
              "Each site includes: 2 Adults (18+), 2 Children (3-17 years old), 1 vehicle, 1 camping unit, 1 picnic table, 1 fire ring. ",
              "Maximum occupancy: 6 people per site (No more than 4 adults, including visitors). ",
              "Additional Vehicles Must be registered with the office and parked by the store or in the visitor lot- NO EXCEPTIONS"
            ]
          },
          {
            "id": "rul-mrfeqk8g-bij5",
            "title": "Food Deliveries ",
            "items": [
              "Delivery drivers will not be allowed past the control gate ",
              "Campers are responsible for meeting drivers at the gate to pick up their orders"
            ]
          }
        ],
        "cta": {
          "title": "Questions about a rule?",
          "sub": "Call the office — we're happy to walk you through anything before you arrive.",
          "primaryLink": {
            "action": "phone",
            "value": ""
          }
        }
      }
    },
    "seo": {
      "site": {
        "siteName": "Butterfly Camping Resort",
        "baseUrl": "https://www.butterflycamp.com",
        "titleTemplate": "%s · Butterfly Camping Resort",
        "defaultDescription": "Family-owned camping resort in the New Jersey Pine Barrens — 128 full-hookup RV sites, 6 wooded tent sites, and 8 cabins on 200 quiet acres. Open April through October.",
        "defaultShareImage": "/uploads/img-mq6332ej-77a4fb.webp",
        "twitterHandle": "",
        "favicon": "/uploads/img-mq6htcl1-bc8562-icon.png",
        "business": {
          "type": "Campground",
          "name": "Butterfly Camping Resort",
          "streetAddress": "360 Butterfly Road",
          "addressLocality": "Jackson",
          "addressRegion": "NJ",
          "postalCode": "08527",
          "country": "US",
          "telephone": "732-928-2107",
          "priceRange": "$$",
          "latitude": "40.096128",
          "longitude": "-74.303856",
          "sameAs": "https://www.facebook.com/ButterflyCampingResort/\nhttps://www.instagram.com/butterfly.camping.resort/\nhttps://www.google.com/maps?cid=10377840934788359529\nhttps://www.tripadvisor.com/Hotel_Review-g46528-d1103469-Reviews-Butterfly_Camping_Resort-Jackson_New_Jersey.html\nhttps://www.yelp.com/biz/butterfly-camping-resort-jackson\nhttps://www.goodsam.com/campgrounds-rv-parks/new-jersey/jackson/butterfly-camping-resort/cgid-740000864\nhttps://campnj.com/campgrounds/butterfly-camping-resort\nhttps://thedyrt.com/camping/new-jersey/butterfly-camping-resort",
          "foundingDate": "1997"
        }
      },
      "pages": {
        "home": {
          "title": "Family Campground in Jackson, NJ",
          "description": "Pool, pond fishing & shady sites 5 miles from Six Flags Great Adventure. Family-run since 1997 — RV, tent & cabin camping. Cabins from $129/night.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "rv": {
          "title": "RV Sites with Full Hookups in Jackson, NJ",
          "description": "128 full-hookup RV sites with 30/50-amp, water & sewer — 5 miles from Six Flags Great Adventure. Shady Pine Barrens sites with pool, free wifi & cable.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "tent": {
          "title": "Tent Camping in Jackson, NJ",
          "description": "6 wooded tent sites with fire rings and picnic tables, tucked into the Pine Barrens.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "cabins": {
          "title": "Cabins from $129/Night in Jackson, NJ",
          "description": "8 air-conditioned cabins from $129/night, 5 miles from Six Flags. Sleeps 4–6 with A/C, mini-fridge & front porch — just bring linens.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "activities": {
          "title": "Camp Activities",
          "description": "Trails, the spring-fed pond, fishing, and family events all season long at Butterfly Camp.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "events": {
          "title": "Weekly Events",
          "description": "See what's on this season — opening weekends, holiday cookouts, and family activities.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "gallery": {
          "title": "Photo Gallery",
          "description": "Photos from around the campground — the pines, the pond, campfires, and happy campers.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "blog": {
          "title": "Field Notes",
          "description": "Tips, season recaps, and stories from the family that runs Butterfly Camping Resort.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        },
        "rules": {
          "title": "Campground Rules & Policies",
          "description": "Check-in times, quiet hours, pet policy, golf cart rules, and campground policies for Butterfly Camping Resort in Jackson, NJ.",
          "canonical": "",
          "ogTitle": "",
          "ogDescription": "",
          "ogImage": "",
          "twitterTitle": "",
          "twitterDescription": "",
          "twitterImage": "",
          "robotsIndex": true
        }
      }
    }
  };
  // BAKE:CONTENT:END

  // ── deep merge (objects merge key-by-key; arrays & scalars replace) ─────────
  const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
  function deepMerge(base, over) {
    if (over === undefined) return base;
    if (!isObj(base) || !isObj(over)) return over;
    const out = { ...base };
    for (const k of Object.keys(over)) {
      out[k] = k in base ? deepMerge(base[k], over[k]) : over[k];
    }
    return out;
  }
  const clone = (v) => JSON.parse(JSON.stringify(v));

  // The effective fallback: hand-authored DEFAULTS with the baked live snapshot
  // layered on top. Used for first paint, hydrate, and "Reset to defaults".
  const EFFECTIVE_DEFAULTS = deepMerge(DEFAULTS, BAKED);

  // ── singleton state + subscribers ──────────────────────────────────────────
  // The public server embeds the live content as <script id="bc-content"
  // type="application/json"> in index.html, so first paint shows real content
  // with no /api/content round-trip. admin.html has no such block → it still
  // hydrates over fetch exactly as before.
  let _boot = null;
  try {
    const bootEl = document.getElementById("bc-content");
    if (bootEl) _boot = JSON.parse(bootEl.textContent);
  } catch (_) { /* malformed boot data — fall back to fetching */ }
  let _data = deepMerge(EFFECTIVE_DEFAULTS, _boot || {});
  const _subs = new Set();
  const _emit = () => { for (const fn of _subs) fn(_data); };
  const _setData = (d) => { _data = d; _emit(); };

  async function _hydrate() {
    try {
      const r = await fetch("/api/content", { cache: "no-store" });
      if (r.ok) _setData(deepMerge(EFFECTIVE_DEFAULTS, await r.json()));
    } catch (_) { /* offline — keep current data */ }
  }

  let _started = false;
  function _start() {
    if (_started) return;
    _started = true;
    if (!_boot) _hydrate(); // boot content already current → skip the refetch
    try {
      const es = new EventSource("/api/stream");
      es.addEventListener("content", () => _hydrate()); // someone saved → refetch
    } catch (_) { /* SSE unsupported — falls back to no live sync */ }
  }

  // ── public API ─────────────────────────────────────────────────────────────
  function getData() { return _data; }

  // Attach the admin's Clerk session token so the server can authorize writes.
  // On the public site Clerk is absent → no header (and the public site never
  // calls these mutating endpoints anyway; GET reads stay open).
  async function authHeaders(extra) {
    let token = null;
    try {
      const c = window.Clerk;
      if (c && c.session && typeof c.session.getToken === "function") token = await c.session.getToken();
    } catch (_) { /* not signed in / Clerk not loaded */ }
    return Object.assign({}, extra || {}, token ? { Authorization: "Bearer " + token } : {});
  }

  async function save(next) {
    const doc = typeof next === "function" ? next(_data) : next;
    _setData(doc); // optimistic
    try {
      await fetch("/api/content", {
        method: "PUT",
        headers: await authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(doc),
      });
    } catch (e) { console.error("BCStore.save failed", e); }
    return doc;
  }

  // shallow-merge a patch into the current document, e.g. update({ hero: {...} })
  const update = (patch) => save((prev) => deepMerge(prev, patch));

  function reset() { return save(clone(EFFECTIVE_DEFAULTS)); }

  // downscale a File to a data URL (cap longest edge), then POST to backend.
  // Preferred output is WebP (keeps transparency AND compresses like JPEG — a
  // multi-MB PNG upload lands as a ~100KB WebP). Browsers that can't encode
  // WebP (older Safari) fall back to the old behavior: formats with an alpha
  // channel (PNG/WebP/GIF/SVG) are kept as PNG so transparency is preserved —
  // exporting them as JPEG would flatten transparent pixels to BLACK (the
  // "black bars" admins were seeing around uploaded logos/photos). Opaque
  // photos still compress as JPEG, on a white backstop so any stray alpha is white.
  function _downscaleBlob(blob, type, max, quality) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        let w = img.width, h = img.height;
        if (w > max || h > max) { const s = Math.min(max / w, max / h); w = Math.round(w * s); h = Math.round(h * s); }
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        const keepAlpha = /png|webp|gif|svg/i.test(type || ""); // formats that carry transparency
        if (!keepAlpha) { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h); } // white backstop for JPEG
        ctx.drawImage(img, 0, 0, w, h);
        // Canvases that don't support WebP encoding return a PNG data URL
        // instead — detect by prefix rather than trusting the request.
        const webp = c.toDataURL("image/webp", quality);
        if (webp.indexOf("data:image/webp") === 0) return resolve(webp);
        resolve(keepAlpha ? c.toDataURL("image/png") : c.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
      img.src = url;
    });
  }

  function _downscale(file, max, quality) {
    const isHeic = /heic|heif/i.test(file.type || "") || /\.(heic|heif)$/i.test(file.name || "");
    if (isHeic && window.heic2any) {
      return window.heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 })
        .then(result => {
          const blob = Array.isArray(result) ? result[0] : result;
          return _downscaleBlob(blob, "image/jpeg", max, quality);
        });
    }
    return _downscaleBlob(file, file.type, max, quality);
  }

  async function uploadImage(file) {
    const dataUrl = await _downscale(file, 1600, 0.82);
    const r = await fetch("/api/upload", {
      method: "POST",
      headers: await authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ name: file.name, dataUrl }),
    });
    if (!r.ok) throw new Error("Upload failed (" + r.status + ")");
    return (await r.json()).src; // "/uploads/xxx.jpg"
  }

  async function deleteUpload(src) {
    if (!src || !/^\/uploads\//.test(src)) return;
    try {
      await fetch("/api/upload", {
        method: "DELETE",
        headers: await authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ src }),
      });
    } catch (_) { /* best effort */ }
  }

  // React hook — [data, actions]
  function useStore() {
    const [data, setLocal] = useState(_data);
    useEffect(() => {
      _start();
      const fn = (d) => setLocal(d);
      _subs.add(fn);
      setLocal(_data); // catch any change between first render and subscribe
      return () => _subs.delete(fn);
    }, []);
    return [data, ACTIONS];
  }

  const ACTIONS = { save, update, reset, uploadImage, deleteUpload };

  // small shared helpers
  const telHref = (phone) => (phone || "").replace(/[^\d]/g, "");
  const uid = (p) => (p || "id") + "-" + Date.now().toString(36) + "-" + Math.floor(Math.random() * 1e6).toString(36);

  // ── Blog post slugs ────────────────────────────────────────────────────────
  // Every published post gets its own URL: /blog/<slug>. The effective slug is
  // the stored one, else derived from the title, else the id — so posts saved
  // before slugs existed still resolve. Mirrored in server.js — keep in sync.
  const slugify = (s) => String(s || "").toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "").slice(0, 80);
  const postSlug = (p) => (p && (p.slug || slugify(p.title) || p.id)) || "";
  function findPost(list, seg) {
    const s = String(seg || "").toLowerCase();
    return (list || []).find((p) => postSlug(p) === s)
        || (list || []).find((p) => String(p.id).toLowerCase() === s) || null;
  }

  // ── CTA link config ────────────────────────────────────────────────────────
  // Every call-to-action stores a link as { action, value }:
  //   phone → tel:    (blank value ⇒ contact.phone, else override)
  //   email → mailto: (blank value ⇒ contact.email, else override)
  //   page  → internal router page (value is one of PAGE_KEYS) ⇒ go(value)
  //   url   → custom URL, opens in a new tab
  const PAGE_KEYS = ["home", "rv", "tent", "cabins", "activities", "events", "gallery", "blog", "rules"];
  const PAGE_LABELS = {
    home: "Home", rv: "RV Sites", tent: "Tent Sites", cabins: "Cabins",
    activities: "Activities", events: "Events", gallery: "Gallery", blog: "Blog",
    rules: "Rules & Regulations",
  };

  // ── path ↔ page-id (the public site uses real paths: home → "/", rv → "/rv") ──
  // Single source of truth shared by bc-app.jsx routing, bc-kit.jsx links, and the
  // CTA resolver below. The server mirrors this map in server.js (PATH_TO_ID).
  const idToPath = (id) => {
    if (id && id.indexOf("blog/") === 0) return "/" + id;
    return (id && id !== "home" && PAGE_KEYS.includes(id)) ? "/" + id : "/";
  };
  function pathToId(pathname) {
    let p = (pathname || "/").replace(/\/+$/, "") || "/";
    if (p === "/") return "home";
    const id = p.slice(1).toLowerCase();
    if (PAGE_KEYS.includes(id)) return id;
    const m = /^blog\/([a-z0-9._-]+)$/.exec(id);
    if (m) return "blog/" + m[1];
    return null;
  }

  // Resolve a link config into props to spread onto <Btn> (href / onClick / target).
  //   link:     { action, value } — may be undefined on older content.json
  //   ctx:      { data, go }      — live store data (for contact) + router go()
  //   fallback: { action, value } — reproduces a CTA's original behavior when blank
  function resolveCtaProps(link, ctx, fallback) {
    const cfg = (link && link.action) ? link : (fallback || { action: "phone", value: "" });
    const contact = ((ctx && ctx.data) || {}).contact || {};
    const go = ctx && ctx.go;
    switch (cfg.action) {
      case "email":
        return { href: "mailto:" + ((cfg.value || "").trim() || contact.email || "") };
      case "page":
        return {
          href: idToPath(cfg.value || "home"),
          onClick: (e) => {
            if (e && (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
            if (e && e.preventDefault) e.preventDefault();
            if (go) go(cfg.value || "home");
          },
        };
      case "url": {
        const u = (cfg.value || "").trim();
        // A blank custom URL must never become a silently dead button — fall back
        // to the phone so every CTA still leads somewhere useful.
        return u ? { href: u, target: "_blank", rel: "noreferrer" }
                 : { href: "tel:" + telHref(contact.phone || "") };
      }
      case "phone":
      default:
        return { href: "tel:" + telHref((cfg.value || "").trim() || contact.phone || "") };
    }
  }

  // Parse the human-entered office hours ([["Mon – Thu","10am – 5pm"], …]) into
  // schema.org OpeningHoursSpecification entries, bounded by the camping season.
  // Rows that don't parse are skipped. Mirrored in server.js — keep in sync.
  const LD_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  function ldHours(rows, season) {
    const DAY_IDX = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6 };
    const dayOf = (s) => { const k = String(s || "").trim().toLowerCase().slice(0, 3); return k in DAY_IDX ? LD_DAYS[DAY_IDX[k]] : null; };
    const t24 = (h, m, ap) => { let hh = parseInt(h, 10) % 12; if (/pm/i.test(ap)) hh += 12; return (hh < 10 ? "0" : "") + hh + ":" + (m || "00"); };
    const iso = (d) => { const t = Date.parse(d); return isNaN(t) ? "" : new Date(t).toISOString().slice(0, 10); };
    const from = season ? iso(season.opens) : "", thru = season ? iso(season.closes) : "";
    const out = [];
    for (const row of rows || []) {
      const label = String((row && row[0]) || ""), val = String((row && row[1]) || "");
      const tm = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*[–—-]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i.exec(val);
      if (!tm) continue;
      let days = [];
      const parts = label.split(/[–—-]/);
      if (parts.length === 2 && dayOf(parts[0]) && dayOf(parts[1])) {
        const i = LD_DAYS.indexOf(dayOf(parts[0])), j = LD_DAYS.indexOf(dayOf(parts[1]));
        if (j >= i) days = LD_DAYS.slice(i, j + 1);
      } else {
        label.split(/[,&/]/).forEach((s) => { const d = dayOf(s); if (d) days.push(d); });
      }
      if (!days.length) continue;
      const spec = { "@type": "OpeningHoursSpecification", dayOfWeek: days, opens: t24(tm[1], tm[2], tm[3]), closes: t24(tm[4], tm[5], tm[6]) };
      if (from) spec.validFrom = from;
      if (thru) spec.validThrough = thru;
      out.push(spec);
    }
    return out;
  }

  // Resolve the effective SEO for a page id: per-page override → seo.site default
  // → derived fallback. Shared by the public site's applySeo (bc-app.jsx) and the
  // admin's live preview. The server mirrors this in server.js (buildSeoBlock) for
  // crawler-facing HTML — keep the two in sync.
  function resolveSeo(id, data) {
    const seo  = (data && data.seo) || {};
    const site = seo.site || {};
    const baseUrl = String(site.baseUrl || "").replace(/\/+$/, "");
    const abs = (u) => (u && u.charAt(0) === "/" && baseUrl) ? baseUrl + u : (u || "");
    const siteName = site.siteName || "Butterfly Camping Resort";

    // Blog post page (id "blog/<slug>"): SEO derives from the post itself —
    // title, excerpt, featured image — plus BlogPosting structured data.
    if (id && id.indexOf("blog/") === 0) {
      const post = findPost(((data && data.blog) || []).filter((p) => p.status === "published"), id.slice(5));
      if (post) {
        const postTitle = (post.title || "").trim() || siteName;
        const title = (site.titleTemplate && site.titleTemplate.indexOf("%s") !== -1)
          ? site.titleTemplate.replace("%s", postTitle) : postTitle;
        const firstBodyLine = String(post.body || "").split("\n")
          .map((l) => l.trim()).find((l) => l && !/^\[photo:/i.test(l)) || "";
        const description = String(post.excerpt || firstBodyLine || site.defaultDescription || "").slice(0, 160);
        const canonical = baseUrl ? baseUrl + "/blog/" + postSlug(post) : "";
        const ogImage = abs(post.src || site.defaultShareImage || "");
        const jsonLd = {
          "@context": "https://schema.org", "@type": "BlogPosting",
          headline: post.title, description,
        };
        if (canonical) { jsonLd.mainEntityOfPage = canonical; jsonLd.url = canonical; }
        if (ogImage) jsonLd.image = ogImage;
        const pub = Date.parse(post.date);
        if (!isNaN(pub)) jsonLd.datePublished = new Date(pub).toISOString().slice(0, 10);
        if (post.updatedAt) jsonLd.dateModified = String(post.updatedAt).slice(0, 10);
        const bn = site.business && site.business.name;
        if (bn) {
          jsonLd.publisher = { "@type": "Organization", name: bn };
          jsonLd.author = { "@type": "Organization", name: bn };
        }
        const postLd = baseUrl ? [jsonLd, {
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: baseUrl + "/" },
            { "@type": "ListItem", position: 2, name: "Journal", item: baseUrl + "/blog" },
            { "@type": "ListItem", position: 3, name: post.title, item: canonical },
          ],
        }] : jsonLd;
        return {
          siteName, title, description, canonical,
          robots: "index,follow",
          ogTitle: title, ogDescription: description, ogType: "article", ogUrl: canonical, ogImage,
          twitterCard: "summary_large_image",
          twitterTitle: title, twitterDescription: description, twitterImage: ogImage,
          twitterSite: site.twitterHandle || "",
          favicon: site.favicon || (data.brand && data.brand.logo) || "",
          jsonLd: postLd,
        };
      }
      id = "blog"; // unknown/unpublished slug → fall through as the blog page
    }
    const page = (seo.pages && seo.pages[id]) || {};

    const pageTitle = (page.title || "").trim();
    let title;
    if (pageTitle && site.titleTemplate && site.titleTemplate.indexOf("%s") !== -1)
      title = site.titleTemplate.replace("%s", pageTitle);
    else title = pageTitle || siteName;

    const description = page.description || site.defaultDescription || "";
    const canonical   = page.canonical || (baseUrl ? baseUrl + idToPath(id) : "");
    const ogTitle     = page.ogTitle || title;
    const ogDescription = page.ogDescription || description;
    const ogImage     = abs(page.ogImage || site.defaultShareImage || "");

    const b = site.business || {};
    let addr = null;
    if (b.streetAddress || b.addressLocality || b.postalCode) {
      addr = { "@type": "PostalAddress" };
      if (b.streetAddress)   addr.streetAddress   = b.streetAddress;
      if (b.addressLocality) addr.addressLocality = b.addressLocality;
      if (b.addressRegion)   addr.addressRegion   = b.addressRegion;
      if (b.postalCode)      addr.postalCode      = b.postalCode;
      if (b.country)         addr.addressCountry  = b.country;
    }
    let jsonLd = null;
    if (b.name) {
      jsonLd = { "@context": "https://schema.org", "@type": b.type || "LocalBusiness", name: b.name };
      if (baseUrl) jsonLd["@id"] = baseUrl + "/#business";
      if (canonical || baseUrl) jsonLd.url = canonical || baseUrl;
      if (site.defaultDescription) jsonLd.description = site.defaultDescription;
      if (ogImage) jsonLd.image = ogImage;
      const logo = abs((data.brand && data.brand.logo) || "");
      if (logo) jsonLd.logo = logo;
      if (b.telephone) jsonLd.telephone = b.telephone;
      const email = b.email || ((data.contact || {}).email);
      if (email) jsonLd.email = email;
      if (b.priceRange) jsonLd.priceRange = b.priceRange;
      if (b.foundingDate) jsonLd.foundingDate = String(b.foundingDate);
      // Social / directory profiles: one URL per line in the admin field.
      const sameAs = String(b.sameAs || "").split(/[\s,]+/).filter((u) => /^https?:\/\//.test(u));
      if (sameAs.length) jsonLd.sameAs = sameAs;
      if (addr) jsonLd.address = addr;
      if (b.latitude && b.longitude)
        jsonLd.geo = { "@type": "GeoCoordinates", latitude: b.latitude, longitude: b.longitude };
      const hours = ldHours(data.officeHours, data.season);
      if (hours.length) jsonLd.openingHoursSpecification = hours;
      // Amenity counts come from the About stats (128 RV sites / 6 tent sites / 8 cabins).
      const stats = (((data.home || {}).about || {}).stats) || [];
      const amen = stats.filter((s) => s && s[0] && s[1]).map((s) => ({ "@type": "LocationFeatureSpecification", name: s[1], value: s[0] }));
      if (amen.length) jsonLd.amenityFeature = amen;
      const rv = ((data.home || {}).reviews) || {};
      if (rv.ratingValue && rv.ratingCount)
        jsonLd.aggregateRating = { "@type": "AggregateRating", ratingValue: String(rv.ratingValue), reviewCount: String(rv.ratingCount) };
    }

    // Extra graph nodes: breadcrumbs on subpages, Event entries on the events page.
    const nodes = jsonLd ? [jsonLd] : [];
    if (id && id !== "home" && baseUrl) {
      nodes.push({
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: baseUrl + "/" },
          { "@type": "ListItem", position: 2, name: PAGE_LABELS[id] || id, item: baseUrl + idToPath(id) },
        ],
      });
    }
    if (id === "events") {
      const seasonYear = (() => { const t = Date.parse((data.season || {}).opens); return isNaN(t) ? new Date().getFullYear() : new Date(t).getFullYear(); })();
      for (const mo of data.events || []) {
        for (const wk of (mo && mo.weeks) || []) {
          if (!wk || wk.on === false) continue;
          const dm = /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})/i.exec(String(wk.title || ""));
          if (!dm) continue;
          const t = Date.parse(dm[1] + " " + dm[2] + ", " + seasonYear);
          if (isNaN(t)) continue;
          const name = String(wk.title).split("|").slice(1).join("|").trim() || String(wk.title);
          const ev = {
            "@context": "https://schema.org", "@type": "Event", name,
            startDate: new Date(t).toISOString().slice(0, 10),
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            location: { "@type": "Place", name: b.name || siteName },
          };
          if (addr) ev.location.address = addr;
          const desc = ((wk.bullets || []).join(" ") || "").trim();
          if (desc) ev.description = desc;
          if (baseUrl) ev.organizer = { "@type": "Organization", name: b.name || siteName, url: baseUrl };
          nodes.push(ev);
        }
      }
    }
    // FAQPage on the rules page — mirror of the same block in server.js
    // resolveSeo(); keep in sync.
    if (id === "rules") {
      const secs = (((data.pages || {}).rules || {}).sections) || [];
      const qs = secs
        .filter((s) => s && s.title && (s.items || []).length)
        .map((s) => ({
          "@type": "Question", name: String(s.title).trim(),
          acceptedAnswer: { "@type": "Answer", text: (s.items || []).map((i) => String(i).trim()).filter(Boolean).join(" ") },
        }));
      if (qs.length) nodes.push({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: qs });
    }
    jsonLd = nodes.length > 1 ? nodes : (nodes[0] || null);

    return {
      siteName, title, description, canonical,
      robots: page.robotsIndex === false ? "noindex,nofollow" : "index,follow",
      ogTitle, ogDescription, ogType: "website", ogUrl: canonical, ogImage,
      twitterCard: "summary_large_image",
      twitterTitle: page.twitterTitle || ogTitle,
      twitterDescription: page.twitterDescription || ogDescription,
      twitterImage: abs(page.twitterImage || page.ogImage || site.defaultShareImage || ""),
      twitterSite: site.twitterHandle || "",
      // NOT absolutized: a <link rel="icon"> resolves against the current origin,
      // so a root-relative path works on localhost, previews, and production alike.
      favicon: site.favicon || (data.brand && data.brand.logo) || "",
      jsonLd,
    };
  }

  window.BCStore = {
    DEFAULTS, getData, save, update, reset, uploadImage, deleteUpload, useStore,
    telHref, uid, deepMerge, clone, slugify, postSlug, findPost,
    resolveCtaProps, PAGE_KEYS, PAGE_LABELS, idToPath, pathToId, resolveSeo,
  };
})();
